import { gql } from '@apollo/client'

import { CACHE_KEYS } from 'constants/static'

import {
  FRAGMENT_CALL_BEHAVIOR_FIELDS,
  FRAGMENT_CALL_SCORECARD_METHODOLOGY_FIELDS,
  FRAGMENT_USER_FIELDS,
  FRAGMENT_TASK_FIELDS,
  FRAGMENT_USER_DATES_ROLES_FIELDS,
  FRAGMENT_TEMPLATE_TASK_FIELDS,
  FRAGMENT_TEMPLATE_CATEGORY_FOLDER,
  FRAGMENT_SNIPPET_CONDITIONS_FIELDS,
  FRAGMENT_TEMPLATE_CATEGORY_LIST_FIELDS,
  FRAGMENT_COHORT_SCHEDULE_FIELDS,
  FRAGMENT_BADGE_FIELDS,
  FRAGMENT_CERTIFICATE_FIELDS,
  FRAGMENT_AWARD_CONDITION_FIELDS,
  FRAGMENT_ASSIGNED_BADGE_FIELDS,
  FRAGMENT_USER_BADGE_FIELDS,
  FRAGMENT_SALES_AUTOMATION_SETTING_FIELDS,
  PUBLIC_EMPLOYEE_FORM_FIELDS,
} from './fragments'

// mutation for deleting a task
export const LOGIN_BY_TOKEN = gql`
  mutation TokenLogin($attributes: UserTokenInput!) {
    ${CACHE_KEYS.TOKEN}(attributes: $attributes) {
      id
    }
  }
`

// Revokes the server-side auth token on logout so a captured token can't be
// replayed afterwards. Backend `logout` mutation returns a Boolean payload.
export const LOGOUT = gql`
  mutation Logout {
    logout
  }
`

// mutation for deleting a task
export const LOGIN_BY_CREDS = gql`
  mutation login($attributes: LoginInput!) {
    login(attributes: $attributes) {
      id
      authenticationToken
      isAdmin
      isTemporaryPassword
      resetPasswordToken
      availableClients {
        id
        name
      }
    }
  }
`

export const LOGIN_WITH_OKTA_TOKEN = gql`
  mutation OktaTokenLogin($attributes: OktaTokenInput!) {
    oktaTokenLogin(attributes: $attributes) {
      id
      authenticationToken
      isAdmin
    }
  }
`

export const LOGIN_WITH_WORKOS_TOKEN = gql`
  mutation WorkosTokenLogin($attributes: WorkosTokenInput!) {
    workosTokenLogin(attributes: $attributes) {
      id
      authenticationToken
    }
  }
`

export const LOGIN_WITH_WORKOS_REDIRECT_URL = gql`
  mutation WorkosRedirectUrl($attributes: WorkosRedirectUrlInput!) {
    workosRedirectUrl(attributes: $attributes) {
      redirectUrl
    }
  }
`

export const LOGIN_WITH_WORKOS_MAGIC_LINK = gql`
  mutation WorkosMagicLink($attributes: WorkosMagicLinkInput!) {
    workosMagicLink(attributes: $attributes) {
      email
    }
  }
`

export const IMPERSONATE_USER = gql`
  mutation ImpersonateUser($userId: ID!, $ttlMinutes: Int, $reason: String) {
    impersonateUser(userId: $userId, ttlMinutes: $ttlMinutes, reason: $reason) {
      token
      expiresAt
    }
  }
`

export const IMPERSONATE_ADMIN = gql`
  mutation ImpersonateAdmin($userId: ID!) {
    impersonateAdmin(userId: $userId) {
      token
      expiresAt
    }
  }
`

export const UPLOAD_LOGO_TO_LOCATION_HIERARCHY = gql`
  mutation UploadLogoToLocationHierarchy(
    $locationHierarchyId: ID!
    $logoFile: Upload!
  ) {
    uploadLogoToLocationHierarchy(
      locationHierarchyId: $locationHierarchyId
      logoFile: $logoFile
    ) {
      logoUrl
    }
  }
`

export const UPLOAD_IMAGE_TO_ACTIVE_STORAGE = gql`
  mutation UploadImageToActiveStorage($imageFile: Upload!) {
    uploadImageToActiveStorage(imageFile: $imageFile) {
      imageUrl
    }
  }
`

export const UPLOAD_FILE_TO_ACTIVE_STORAGE = gql`
  mutation UploadFileToActiveStorage($file: Upload!) {
    uploadFileToActiveStorage(file: $file) {
      fileUrl
    }
  }
`

export const CREATE_LOCATION_HIERARCHY_TREE = gql`
  mutation CreateLocationHierarchyTree($tree: String!) {
    createLocationHierarchyTree(tree: $tree) {
      success
    }
  }
`

// mutation for deleting a location hierarchy
export const DELETE_LOCATION_HIERARCHY = gql`
  mutation DELETE_LOCATION_HIERARCHY($id: ID!) {
    deleteLocationHierarchy(id: $id) {
      id
    }
  }
`

// mutation for deleting a location hierarchy
export const DUPLICATE_LOCATION_AND_TASKS = gql`
  mutation DuplicateLocationAndTasks($id: ID!, $title: String!) {
    duplicateLocationAndTasks(id: $id, title: $title) {
      id
    }
  }
`

export const UPDATE_LOCATION_HIERARCHY = gql`
  mutation UpdateLocationHierarchy($attributes: LocationHierarchyInput!) {
    updateLocationHierarchy(attributes: $attributes) {
      title
      state
    }
  }
`

// mutation for creating a task
export const CREATE_TASK = gql`
  mutation CreateTask($attributes: TaskInput!) {
    createTask(attributes: $attributes) {
      ...TaskFields
    }
  }
  ${FRAGMENT_TASK_FIELDS}
`

export const CREATE_TASKS_FROM_TEMPLATES = gql`
  mutation CreateTasksFromTemplates($attributes: TasksFromTemplatesInput!) {
    createTasksFromTemplates(attributes: $attributes) {
      ...TaskFields
    }
  }
  ${FRAGMENT_TASK_FIELDS}
`

// mutation for updating a task
export const UPDATE_TASK = gql`
  mutation UpdateTask($id: ID!, $locale: String!, $attributes: TaskInput!) {
    updateTask(id: $id, locale: $locale, attributes: $attributes) {
      ...TaskFields
    }
  }
  ${FRAGMENT_TASK_FIELDS}
`

// mutation for creating a task
export const CREATE_TEMPLATE_TASK = gql`
  mutation CreateTemplateTask(
    $attributes: TemplateTaskInput!
    $createDefaultRole: Boolean
  ) {
    createTemplateTask(
      attributes: $attributes
      createDefaultRole: $createDefaultRole
    ) {
      ...TemplateTaskFields
    }
  }
  ${FRAGMENT_TEMPLATE_TASK_FIELDS}
`

export const CREATE_TEMPLATE_TASK_FROM_AI = gql`
  mutation CreateTemplateTaskFromAi(
    $attributes: TemplateTaskInput!
    $createDefaultRole: Boolean
  ) {
    createTemplateTaskFromAi(
      attributes: $attributes
      createDefaultRole: $createDefaultRole
    ) {
      ...TemplateTaskFields
    }
  }
  ${FRAGMENT_TEMPLATE_TASK_FIELDS}
`

// mutation for updating a task
export const UPDATE_TEMPLATE_TASK = gql`
  mutation UpdateTemplateTask(
    $ids: [ID!]!
    $locale: String
    $attributes: TaskInput!
  ) {
    updateTemplateTask(ids: $ids, locale: $locale, attributes: $attributes) {
      ...TemplateTaskFields
    }
  }
  ${FRAGMENT_TEMPLATE_TASK_FIELDS}
`

export const UPDATE_TEMPLATE_TASK_IMAGE = gql`
  mutation UpdateTemplateTaskImage(
    $id: ID!
    $attributes: UpdateTemplateTaskImageInput!
  ) {
    updateTemplateTaskImage(id: $id, attributes: $attributes) {
      id
    }
  }
`

// mutation for deleting a task
export const DELETE_TASK = gql`
  mutation DeleteTask($id: ID!) {
    deleteTask(id: $id) {
      ...TaskFields
    }
  }
  ${FRAGMENT_TASK_FIELDS}
`

// mutation for archiving a task
export const ARCHIVE_TASK = gql`
  mutation ArchiveTask($id: ID!) {
    archiveTask(id: $id) {
      ...TaskFields
    }
  }
  ${FRAGMENT_TASK_FIELDS}
`

// mutation for deleting a task
export const DELETE_TEMPLATE_TASK = gql`
  mutation DeleteTemplateTask($ids: [ID!]!, $deleteAssociatedTasks: Boolean!) {
    deleteTemplateTask(
      ids: $ids
      deleteAssociatedTasks: $deleteAssociatedTasks
    ) {
      id
    }
  }
`

export const UPDATE_TASKS_ORDER = gql`
  mutation UpdateTasksOrder(
    $locationHierarchyId: ID!
    $tasks: [TaskInputReordering!]!
  ) {
    updateTasksOrder(locationHierarchyId: $locationHierarchyId, tasks: $tasks) {
      ...TaskFields
    }
  }
  ${FRAGMENT_TASK_FIELDS}
`

export const UPDATE_TEMPLATE_TASKS_ORDER = gql`
  mutation UpdateTemplateTasksOrder(
    $templateCategoryId: ID!
    $templateTasks: [TaskInputReordering!]!
  ) {
    updateTemplateTasksOrder(
      templateCategoryId: $templateCategoryId
      templateTasks: $templateTasks
    ) {
      id
      title
      description
      taskType
      mediaUrl
    }
  }
`

export const UPDATE_TEMPLATE_CATEGORIES_ORDER = gql`
  mutation UpdateTemplateCategoriesOrder(
    $parentTemplateCategoryId: ID
    $templateCategoriesIds: [ID!]!
  ) {
    updateTemplateCategoriesOrder(
      parentTemplateCategoryId: $parentTemplateCategoryId
      templateCategoriesIds: $templateCategoriesIds
    ) {
      id
      category
    }
  }
`

// mutation for creating a customer
export const CREATE_CUSTOMER = gql`
  mutation CreateCustomer($attributes: CustomerInput!) {
    createCustomer(attributes: $attributes) {
      id
      title
    }
  }
`

// mutation for creating a customer
export const PUBLISH_TEMPLATE_TASK = gql`
  mutation PublishTemplateTask($attributes: TemplateTaskInput!) {
    publishTemplateTask(attributes: $attributes) {
      success
    }
  }
`

// mutation for creating a customer
export const DUPLICATE_TEMPLATE_TASKS = gql`
  mutation DuplicateTemplateTasks($attributes: DuplicateTemplateTasksInput!) {
    duplicateTemplateTasks(attributes: $attributes) {
      success
    }
  }
`

export const CREATE_TEMPLATE_CATEGORY = gql`
  mutation CreateTemplateCategory($attributes: TemplateCategoryInput!) {
    createTemplateCategory(attributes: $attributes) {
      ...TemplateCategoryListFields
    }
  }
  ${FRAGMENT_TEMPLATE_CATEGORY_LIST_FIELDS}
`

export const UPDATE_TEMPLATE_CATEGORY = gql`
  mutation UpdateTemplateCategory(
    $id: ID!
    $attributes: TemplateCategoryInput!
  ) {
    updateTemplateCategory(id: $id, attributes: $attributes) {
      ...TemplateCategoryListFields
    }
  }
  ${FRAGMENT_TEMPLATE_CATEGORY_LIST_FIELDS}
`

export const DELETE_TEMPLATE_CATEGORY = gql`
  mutation DeleteTemplateCategory(
    $id: ID!
    $moveTasksToTemplateCategoryId: ID
    $deleteTasks: Boolean
    $reimportTasks: Boolean
  ) {
    deleteTemplateCategory(
      id: $id
      moveTasksToTemplateCategoryId: $moveTasksToTemplateCategoryId
      deleteTasks: $deleteTasks
      reimportTasks: $reimportTasks
    ) {
      success
      hasTasks
    }
  }
`

export const UPDATE_LOCATION_HIERARCHY_NOTES = gql`
  mutation UpdateLocationHierarchy($attributes: LocationHierarchyInput!) {
    updateLocationHierarchy(attributes: $attributes) {
      notes
    }
  }
`

export const OKTA_CONFIGURATION = gql`
  mutation OktaConfiguration(
    $issuer: String
    $email: String
    $ssoIdentifier: String
  ) {
    oktaConfiguration(
      issuer: $issuer
      email: $email
      ssoIdentifier: $ssoIdentifier
    ) {
      oktaClientId
      issuer
      googleSignInDisabled
    }
  }
`

export const DUPLICATE_TEMPLATE_TASK = gql`
  mutation DuplicateTemplateTask($attributes: DuplicateTemplateTaskInput!) {
    duplicateTemplateTask(attributes: $attributes) {
      ...TemplateTaskFields
      # Single-task result: include the trigger-coverage flag so the duplicated
      # task shows the triggers indicator immediately (it's covered via its
      # template when cloned into a covered template) without a page refresh.
      # Kept off the shared fragment to avoid N+1 in list queries.
      coveredByCohortSchedule
    }
  }
  ${FRAGMENT_TEMPLATE_TASK_FIELDS}
`

export const UPDATE_USER_PASSWORD = gql`
  mutation UpdateUserPassword($id: ID!, $attributes: UpdateUserPasswordInput!) {
    updateUserPassword(id: $id, attributes: $attributes) {
      id
    }
  }
`

export const RESET_PASSWORD_VIA_TOKEN = gql`
  mutation ResetPasswordViaToken(
    $token: String!
    $attributes: UpdateUserPasswordInput!
  ) {
    resetPasswordViaToken(token: $token, attributes: $attributes) {
      id
      authenticationToken
    }
  }
`

export const UPDATE_WELCOME_EMAIL_SETTINGS = gql`
  mutation UpdateClientWelcomeEmail(
    $attributes: ClientWelcomeEmailSettingInput!
  ) {
    updateClientWelcomeEmail(attributes: $attributes) {
      clientId
      text
      daysBeforeStartDate
      emailType
    }
  }
`

export const DUPLICATE_TEMPLATE_CATEGORY = gql`
  mutation DuplicateTemplateCategory(
    $attributes: DuplicateTemplateCategoryInput!
  ) {
    duplicateTemplateCategory(attributes: $attributes) {
      ...TemplateCategoryListFields
    }
  }
  ${FRAGMENT_TEMPLATE_CATEGORY_LIST_FIELDS}
`

export const SET_TEMPLATE_CATEGORY_REPORT_EXCLUSIONS = gql`
  mutation SetTemplateCategoryReportExclusions(
    $templateCategoryId: ID!
    $templateTaskIds: [ID!]!
  ) {
    setTemplateCategoryReportExclusions(
      templateCategoryId: $templateCategoryId
      templateTaskIds: $templateTaskIds
    ) {
      id
      excludedTemplateTaskIds
    }
  }
`

export const DELETE_USER = gql`
  mutation DeleteUser($id: ID!) {
    deleteUser(id: $id) {
      id
    }
  }
`

export const TERMINATE_USER = gql`
  mutation TerminateUser($id: ID!) {
    terminateUser(id: $id) {
      id
    }
  }
`

export const UPDATE_PAGE_SECTION = gql`
  mutation UpdatePageSection($attributes: PageSectionInput!) {
    updatePageSection(attributes: $attributes) {
      id
      content
      contentRaw
      sectionType
      hidden
      pictureUrl
      icon1Url
      icon2Url
      icon3Url
      icon4Url
      icon5Url
      icon6Url
      icon7Url
      icon8Url
      icon9Url
      icon10Url
    }
  }
`

export const UPSERT_USERS_AND_WORKFLOWS_FROM_CSV = gql`
  mutation UpsertUsersAndWorkflowsFromCsv(
    $file: Upload!
    $blankValueMode: String
  ) {
    upsertUsersAndWorkflowsFromCsv(
      file: $file
      blankValueMode: $blankValueMode
    ) {
      channelId
    }
  }
`

export const UPSERT_TEMPLATE_TASKS_FROM_CSV = gql`
  mutation UpsertTemplateTasksFromCsv(
    $file: Upload!
    $missingTaxonomyTermMode: String
  ) {
    upsertTemplateTasksFromCsv(
      file: $file
      missingTaxonomyTermMode: $missingTaxonomyTermMode
    ) {
      channelId
    }
  }
`

// ENGG-10400
export const COMPLETE_TASKS_FROM_CSV = gql`
  mutation CompleteTasksFromCsv($file: Upload!, $missingTaskMode: String) {
    completeTasksFromCsv(file: $file, missingTaskMode: $missingTaskMode) {
      channelId
    }
  }
`

export const UPSERT_COMMUNICATION_SCHEDULES_FROM_CSV = gql`
  mutation UpsertCommunicationSchedulesFromCsv(
    $file: Upload!
    $templateTaskId: ID!
  ) {
    upsertCommunicationSchedulesFromCsv(
      file: $file
      templateTaskId: $templateTaskId
    ) {
      channelId
    }
  }
`

export const UPSERT_TEMPLATE_TASK_QUESTIONS_FROM_CSV = gql`
  mutation UpsertTemplateTaskQuestionsFromCsv(
    $file: Upload!
    $templateTaskId: ID!
  ) {
    upsertTemplateTaskQuestionsFromCsv(
      file: $file
      templateTaskId: $templateTaskId
    ) {
      channelId
    }
  }
`

export const UPSERT_QUESTION_SET_QUESTIONS_FROM_CSV = gql`
  mutation UpsertQuestionSetQuestionsFromCsv(
    $file: Upload!
    $questionSetId: ID!
  ) {
    upsertQuestionSetQuestionsFromCsv(
      file: $file
      questionSetId: $questionSetId
    ) {
      channelId
    }
  }
`

export const UPSERT_SCORECARD_TEMPLATE_QUESTIONS_FROM_CSV = gql`
  mutation UpsertScorecardTemplateQuestionsFromCsv(
    $file: Upload!
    $scorecardTemplateId: ID!
  ) {
    upsertScorecardTemplateQuestionsFromCsv(
      file: $file
      scorecardTemplateId: $scorecardTemplateId
    ) {
      channelId
    }
  }
`

export const RETRY_TEMPLATE_TASKS_UPLOAD = gql`
  mutation RetryTemplateTasksUpload($csvImportJobId: ID!) {
    retryTemplateTasksUpload(csvImportJobId: $csvImportJobId) {
      channelId
      csvImportJobId
    }
  }
`

export const CREATE_MULTIPLE_OPTION = gql`
  mutation CreateMultipleOption($attributes: CreateMultipleOptionInput!) {
    createMultipleOption(attributes: $attributes) {
      id
      clientId
      options {
        id
        option
      }
    }
  }
`

export const CREATE_TEMPLATE_ROLE = gql`
  mutation CreateTemplateRole($attributes: TemplateRoleInput!) {
    createTemplateRole(attributes: $attributes) {
      id
      name
    }
  }
`

export const CREATE_DATE_TYPE = gql`
  mutation CreateDateType($attributes: DateTypeInput!) {
    createDateType(attributes: $attributes) {
      id
      name
    }
  }
`

export const UPDATE_DATE_TYPE = gql`
  mutation UpdateDateType($id: ID!, $attributes: DateTypeInput!) {
    updateDateType(id: $id, attributes: $attributes) {
      id
      name
    }
  }
`

export const DELETE_DATE_TYPE = gql`
  mutation DeleteDateType($id: ID!) {
    deleteDateType(id: $id) {
      success
      errors
    }
  }
`

export const CREATE_EMPLOYEE = gql`
  mutation CreateEmployee(
    $attributes: EmployeeInput!
    $applyDefaultValues: Boolean
  ) {
    createEmployee(
      attributes: $attributes
      applyDefaultValues: $applyDefaultValues
    ) {
      id
      email
      firstName
      lastName
      fullName
      eligibleTemplateCategories {
        id
        category
        internalCategory
      }
    }
  }
`

export const ASSOCIATE_EMPLOYEE = gql`
  mutation AssociateEmployee($attributes: EmployeeInput!) {
    associateEmployee(attributes: $attributes) {
      id
      email
      firstName
      lastName
      fullName
      eligibleTemplateCategories {
        id
        category
        internalCategory
      }
    }
  }
`

export const UPDATE_EMPLOYEE = gql`
  mutation UpdateEmployee(
    $id: ID!
    $attributes: UpdateEmployeeInput!
    $applyDefaultValues: Boolean
  ) {
    updateEmployee(
      id: $id
      attributes: $attributes
      applyDefaultValues: $applyDefaultValues
    ) {
      ...UserFields
      ...UserDatesAndRolesFields
      fullName
      eligibleTemplateCategories {
        id
        category
        internalCategory
      }
    }
  }
  ${FRAGMENT_USER_FIELDS}
  ${FRAGMENT_USER_DATES_ROLES_FIELDS}
`

export const UPDATE_EMPLOYEE_FLAG = gql`
  mutation UpdateEmployeeFlag($id: ID!, $attributes: UpdateEmployeeFlagInput!) {
    updateEmployeeFlag(id: $id, attributes: $attributes) {
      id
    }
  }
`

export const CREATE_WORKFLOW = gql`
  mutation CreateWorkflow($attributes: WorkflowInput!) {
    createWorkflow(attributes: $attributes) {
      success
    }
  }
`

export const IMPORT_INTO_WORKFLOW = gql`
  mutation ImportIntoWorkflow($attributes: ImportIntoWorkflowInput!) {
    importIntoWorkflow(attributes: $attributes) {
      id
    }
  }
`

export const UPDATE_WORKFLOW = gql`
  mutation UpdateWorkflow($attributes: WorkflowUpdateInput!) {
    updateWorkflow(attributes: $attributes) {
      success
    }
  }
`

export const UPDATE_TASK_ANALYTICS = gql`
  mutation UpdateTaskAnalytics($taskAnalyticsId: ID!, $reviewed: Boolean!) {
    updateTaskAnalytics(
      taskAnalyticsId: $taskAnalyticsId
      reviewed: $reviewed
    ) {
      success
    }
  }
`

export const UPDATE_TAXONOMY_TERM_HIERARCHY = gql`
  mutation UpdateTaxonomyTermHierarchy($tree: String!, $taxonomyId: ID!) {
    updateTaxonomyTermHierarchy(tree: $tree, taxonomyId: $taxonomyId) {
      success
    }
  }
`

export const UPDATE_TAXONOMY_TERM = gql`
  mutation UpdateTaxonomyTerm($attributes: TaxonomyTermInput!) {
    updateTaxonomyTerm(attributes: $attributes) {
      title
    }
  }
`

export const MIGRATE_TAXONOMY_TERM_USAGE = gql`
  mutation MigrateTaxonomyTermUsage(
    $sourceTaxonomyTermId: ID!
    $targetTaxonomyTermId: ID!
    $usageType: String!
  ) {
    migrateTaxonomyTermUsage(
      sourceTaxonomyTermId: $sourceTaxonomyTermId
      targetTaxonomyTermId: $targetTaxonomyTermId
      usageType: $usageType
    )
  }
`

export const DELETE_TAXONOMY_TERM = gql`
  mutation DeleteTaxonomyTerm($id: ID!) {
    deleteTaxonomyTerm(id: $id) {
      id
    }
  }
`

export const DELETE_TAXONOMY = gql`
  mutation DeleteTaxonomy($id: ID!) {
    deleteTaxonomy(id: $id) {
      id
    }
  }
`

export const CREATE_TAXONOMY = gql`
  mutation CreateTaxonomy($attributes: TaxonomyInput!) {
    createTaxonomy(attributes: $attributes) {
      id
      name
      key
      baseTaxonomyId
      editable
      isLocation
      slug
      source
      uniqueTerms
    }
  }
`

export const UPDATE_TAXONOMY = gql`
  mutation UpdateTaxonomy($id: ID!, $attributes: TaxonomyInput!) {
    updateTaxonomy(id: $id, attributes: $attributes) {
      id
      name
      key
      baseTaxonomyId
      editable
      isLocation
      slug
      source
      uniqueTerms
    }
  }
`

export const UPDATE_TAXONOMIES_ORDER = gql`
  mutation UpdateTaxonomiesOrder($taxonomies: [TaxonomyTermInput!]!) {
    updateTaxonomiesOrder(taxonomies: $taxonomies) {
      success
    }
  }
`

export const CREATE_SNIPPET = gql`
  mutation CreateSnippet($attributes: SnippetInput!) {
    createSnippet(attributes: $attributes) {
      id
      name
    }
  }
`

export const UPSERT_SNIPPET_CONDITION = gql`
  mutation UpsertSnippetCondition($attributes: SnippetConditionInput!) {
    upsertSnippetCondition(attributes: $attributes) {
      ...SnippetConditionsFields
    }
  }
  ${FRAGMENT_SNIPPET_CONDITIONS_FIELDS}
`

export const DELETE_SNIPPET_CONDITION = gql`
  mutation DeleteSnippetCondition($id: ID!) {
    deleteSnippetCondition(id: $id) {
      success
    }
  }
`

export const DELETE_SNIPPET = gql`
  mutation DeleteSnippet($id: ID!) {
    deleteSnippet(id: $id) {
      success
    }
  }
`

export const UPDATE_SNIPPET = gql`
  mutation UpdateSnippet($id: ID!, $attributes: SnippetInput!) {
    updateSnippet(id: $id, attributes: $attributes) {
      id
      name
    }
  }
`

export const CREATE_AUTHORIZATION_TOKEN = gql`
  mutation CreateAuthorizationToken($attributes: AuthorizationTokenInput!) {
    createAuthorizationToken(attributes: $attributes) {
      success
    }
  }
`

export const REVOKE_AUTHORIZATION_TOKEN = gql`
  mutation RevokeAuthorizationToken($id: ID!) {
    revokeAuthorizationToken(id: $id) {
      success
    }
  }
`

export const CREATE_MCP_TOKEN = gql`
  mutation CreateMcpToken($name: String!, $toolNames: [String!]) {
    createMcpToken(name: $name, toolNames: $toolNames) {
      token
      mcpToken {
        id
        name
        tokenPreview
        lastUsedAt
        createdAt
        allowedTools
        servers
      }
    }
  }
`

export const REVOKE_MCP_TOKEN = gql`
  mutation RevokeMcpToken($id: ID!) {
    revokeMcpToken(id: $id) {
      id
    }
  }
`

export const UPDATE_MCP_TOKEN_TOOLS = gql`
  mutation UpdateMcpTokenTools($id: ID!, $toolNames: [String!]!) {
    updateMcpTokenTools(id: $id, toolNames: $toolNames) {
      id
      allowedTools
      servers
    }
  }
`

export const CREATE_AUTO_IMPORT_RULE = gql`
  mutation CreateAutoImportRule($attributes: AutoImportRuleInput!) {
    createAutoImportRule(attributes: $attributes) {
      id
    }
  }
`

export const UPDATE_AUTO_IMPORT_RULE = gql`
  mutation UpdateAutoImportRule($id: ID!, $attributes: AutoImportRuleInput!) {
    updateAutoImportRule(id: $id, attributes: $attributes) {
      id
    }
  }
`

export const DELETE_AUTO_IMPORT_RULE = gql`
  mutation DeleteAutoImportRule($id: ID!) {
    deleteAutoImportRule(id: $id) {
      success
    }
  }
`

export const RUN_AUTO_IMPORT = gql`
  mutation RunAutoImport($employeeUserId: ID) {
    runAutoImport(employeeUserId: $employeeUserId) {
      id
      status
      phase
      progressPercentage
      phaseLabel
      startedAt
      finishedAt
    }
  }
`

export const UPDATE_TAXONOMY_IMPORT_ENABLED = gql`
  mutation UpdateTaxonomyImportEnabled($status: Boolean!) {
    updateTaxonomyImportEnabled(status: $status)
  }
`

export const UPDATE_CLIENT_SETTING = gql`
  mutation UpdateClientSetting($attributes: ClientSettingInput!) {
    updateClientSetting(attributes: $attributes)
  }
`

// ENGG-10257 — the folder-level taxonomyTerms are deprecated on the server
// (the audience lives on each item now), so they are no longer selected.
export const SET_GOOGLE_DRIVE_FOLDERS_WHITELIST = gql`
  mutation SetGoogleDriveFoldersWhitelist(
    $installationId: ID!
    $folders: [GoogleDriveWhitelistedFolderInput!]!
  ) {
    setGoogleDriveFoldersWhitelist(
      installationId: $installationId
      folders: $folders
    ) {
      id
      folderId
      folderName
    }
  }
`

export const SET_SHAREPOINT_WHITELIST = gql`
  mutation SetSharepointWhitelist(
    $installationId: ID!
    $resources: [SharepointResourceInput!]!
  ) {
    setSharepointWhitelist(
      installationId: $installationId
      resources: $resources
    ) {
      id
      resourceType
      siteId
      name
      webUrl
    }
  }
`

export const DELETE_SHAREPOINT_INSTALLATION = gql`
  mutation DeleteSharepointInstallation($installationId: ID!) {
    deleteSharepointInstallation(installationId: $installationId) {
      success
      id
    }
  }
`

export const DELETE_GOOGLE_DRIVE_INSTALLATION = gql`
  mutation DeleteGoogleDriveInstallation($installationId: ID!) {
    deleteGoogleDriveInstallation(installationId: $installationId) {
      success
      id
    }
  }
`

export const SET_NOTION_RESOURCES_WHITELIST = gql`
  mutation SetNotionResourcesWhitelist(
    $installationId: ID!
    $resources: [NotionWhitelistedResourceInput!]!
  ) {
    setNotionResourcesWhitelist(
      installationId: $installationId
      resources: $resources
    ) {
      id
      notionId
      resourceType
      name
    }
  }
`

export const START_SPEKIT_OAUTH = gql`
  mutation StartSpekitOauth {
    startSpekitOauth {
      authorizationUrl
    }
  }
`

export const DELETE_SPEKIT_INSTALLATION = gql`
  mutation DeleteSpekitInstallation($installationId: ID!) {
    deleteSpekitInstallation(installationId: $installationId) {
      success
      id
    }
  }
`

export const DELETE_NOTION_INSTALLATION = gql`
  mutation DeleteNotionInstallation($installationId: ID!) {
    deleteNotionInstallation(installationId: $installationId) {
      success
      id
    }
  }
`

export const CREATE_NOTION_INSTALLATION_FROM_TOKEN = gql`
  mutation CreateNotionInstallationFromToken(
    $accessToken: String!
    $workspaceName: String
  ) {
    createNotionInstallationFromToken(
      accessToken: $accessToken
      workspaceName: $workspaceName
    ) {
      id
      workspaceId
      workspaceName
      clientId
      createdAt
      updatedAt
    }
  }
`

export const CREATE_CONFLUENCE_INSTALLATION = gql`
  mutation CreateConfluenceInstallation(
    $siteUrl: String!
    $email: String!
    $apiToken: String!
    $siteName: String
  ) {
    createConfluenceInstallation(
      siteUrl: $siteUrl
      email: $email
      apiToken: $apiToken
      siteName: $siteName
    ) {
      id
      siteUrl
      siteName
      email
      clientId
      createdAt
      updatedAt
    }
  }
`

export const SET_CONFLUENCE_SPACES_WHITELIST = gql`
  mutation SetConfluenceSpacesWhitelist(
    $installationId: ID!
    $spaces: [ConfluenceWhitelistedSpaceInput!]!
  ) {
    setConfluenceSpacesWhitelist(
      installationId: $installationId
      spaces: $spaces
    ) {
      id
      spaceId
      spaceKey
      name
    }
  }
`

export const DELETE_CONFLUENCE_INSTALLATION = gql`
  mutation DeleteConfluenceInstallation($installationId: ID!) {
    deleteConfluenceInstallation(installationId: $installationId) {
      success
      id
    }
  }
`

export const CREATE_GONG_INSTALLATION = gql`
  mutation CreateGongInstallation(
    $apiBaseUrl: String
    $accessKey: String!
    $accessKeySecret: String!
    $accountName: String
  ) {
    createGongInstallation(
      apiBaseUrl: $apiBaseUrl
      accessKey: $accessKey
      accessKeySecret: $accessKeySecret
      accountName: $accountName
    ) {
      id
      apiBaseUrl
      accountName
      clientId
      createdAt
      updatedAt
    }
  }
`

export const SET_GONG_WORKSPACES_WHITELIST = gql`
  mutation SetGongWorkspacesWhitelist(
    $installationId: ID!
    $workspaces: [GongWhitelistedWorkspaceInput!]!
  ) {
    setGongWorkspacesWhitelist(
      installationId: $installationId
      workspaces: $workspaces
    ) {
      id
      workspaceId
      name
    }
  }
`

export const DELETE_GONG_INSTALLATION = gql`
  mutation DeleteGongInstallation($installationId: ID!) {
    deleteGongInstallation(installationId: $installationId) {
      success
      id
    }
  }
`

export const CREATE_MERGE_AUTH_TOKEN = gql`
  mutation CreateMergeAuthToken($publicToken: String!) {
    createMergeAuthToken(publicToken: $publicToken) {
      success
    }
  }
`

export const UPSERT_RESOURCE = gql`
  mutation UpsertResource($attributes: ResourceInput!) {
    upsertResource(attributes: $attributes) {
      id
      title
      titleRaw
      url
      position
    }
  }
`

export const DELETE_RESOURCE = gql`
  mutation DeleteResource($id: ID!) {
    deleteResource(id: $id) {
      id
    }
  }
`

export const UPSERT_RESOURCE_SECTION = gql`
  mutation UpsertResourceSection($attributes: ResourceSectionInput!) {
    upsertResourceSection(attributes: $attributes) {
      id
    }
  }
`

export const DELETE_RESOURCE_SECTION = gql`
  mutation DeleteResourceSection($id: ID!) {
    deleteResourceSection(id: $id) {
      id
    }
  }
`

export const UPDATE_ROLE_PERMISSIONS = gql`
  mutation UpdateRolePermissions($roleId: ID!, $permissionsId: [Int!]!) {
    updateRolePermissions(roleId: $roleId, permissionsId: $permissionsId) {
      id
      title
    }
  }
`

export const CREATE_ROLE = gql`
  mutation CreateRole($name: String!) {
    createRole(name: $name) {
      id
      name
    }
  }
`

export const DELETE_ROLE = gql`
  mutation DeleteRole($id: ID!, $newRoleId: ID) {
    deleteRole(id: $id, newRoleId: $newRoleId) {
      id
      name
    }
  }
`

export const UPDATE_ROLE = gql`
  mutation UpdateRole($id: ID!, $name: String!) {
    updateRole(id: $id, name: $name) {
      id
      name
    }
  }
`

export const ADD_TEMPLATE_TASK_TO_USERS = gql`
  mutation AddTemplateTaskToUsers(
    $templateTaskId: ID!
    $userIds: [ID!]!
    $syncMode: String
  ) {
    addTemplateTaskToUsers(
      templateTaskId: $templateTaskId
      userIds: $userIds
      syncMode: $syncMode
    ) {
      success
    }
  }
`

export const FORCE_IMPORT_TEMPLATE_TASK = gql`
  mutation ForceImportTemplateTask(
    $templateTaskId: ID!
    $userId: ID!
    $setDates: Boolean
    $assignDate: DetailedDateTimeInput
    $dueDate: DetailedDateTimeInput
  ) {
    forceImportTemplateTask(
      templateTaskId: $templateTaskId
      userId: $userId
      setDates: $setDates
      assignDate: $assignDate
      dueDate: $dueDate
    ) {
      id
      templateTask {
        id
      }
      lastError {
        error
        level
      }
    }
  }
`
// ENGG-8840: force-import an entire section (all its tasks) onto one employee, with the per-task
// admin-reviewed dates + dependency-override choices from the two-step review table.
export const FORCE_IMPORT_TEMPLATE_SECTION = gql`
  mutation ForceImportTemplateSection(
    $templateCategoryId: ID!
    $userId: ID!
    $setDates: Boolean
    $taskDates: [ForceImportSectionTaskDatesInput!]
  ) {
    forceImportTemplateSection(
      templateCategoryId: $templateCategoryId
      userId: $userId
      setDates: $setDates
      taskDates: $taskDates
    ) {
      id
      templateTask {
        id
      }
    }
  }
`

export const RESEND_WELCOME_EMAIL = gql`
  mutation ResendWelcomeEmail($userId: ID!, $sendToPersonalEmail: Boolean!) {
    resendWelcomeEmail(
      userId: $userId
      sendToPersonalEmail: $sendToPersonalEmail
    ) {
      success
    }
  }
`

// ENGG-8851: "Send now" — fire one due-date rule for one task immediately
// (returns the addresses sent to).
export const SEND_TASK_REMINDER = gql`
  mutation SendTaskReminder($taskId: ID!, $configId: ID!) {
    sendTaskReminder(taskId: $taskId, configId: $configId)
  }
`

export const SEND_TASK_NOTIFICATION_PREVIEW = gql`
  mutation SendTaskNotificationPreview(
    $templateTaskId: ID!
    $trigger: String!
    $message: String
    $medium: String
  ) {
    sendTaskNotificationPreview(
      templateTaskId: $templateTaskId
      trigger: $trigger
      message: $message
      medium: $medium
    )
  }
`

export const UPDATE_NOTIFICATION_DENY_LIST = gql`
  mutation UpdateNotificationDenyList(
    $attributes: [NotificationDenyListInput!]!
  ) {
    updateNotificationDenyList(attributes: $attributes) {
      title
      type
      typeId: id
      denied
    }
  }
`

export const CREATE_TEMPLATE_TAG = gql`
  mutation CreateTemplateTag($attributes: TemplateTagInput!) {
    createTemplateTag(attributes: $attributes) {
      color
      id
      name
    }
  }
`

export const CREATE_TEMPLATE_ROLE_USER = gql`
  mutation CreateTemplateRoleUser($attributes: TemplateRoleUserInput!) {
    createTemplateRoleUser(attributes: $attributes) {
      id
    }
  }
`

export const UPDATE_TEMPLATE_ROLE_USER = gql`
  mutation UpdateTemplateRoleUser($attributes: TemplateRoleUserInput!) {
    updateTemplateRoleUser(attributes: $attributes) {
      id
    }
  }
`

export const UPDATE_TEMPLATE_ROLE_USERS = gql`
  mutation UpdateTemplateRoleUsers($attributes: [TemplateRoleUserInput!]!) {
    updateTemplateRoleUsers(attributes: $attributes) {
      priority
    }
  }
`

export const UPDATE_TEMPLATE_ROLE_USERS_PRIORITY = gql`
  mutation UpdateTemplateRoleUsersPriority(
    $templateRoleUsers: [TemplateRoleUserPriorityInput!]!
  ) {
    updateTemplateRoleUsersPriority(templateRoleUsers: $templateRoleUsers) {
      priority
    }
  }
`

export const DELETE_TEMPLATE_ROLE_USER = gql`
  mutation DeleteTemplateRoleUser($id: ID!) {
    deleteTemplateRoleUser(id: $id) {
      success
    }
  }
`

export const APPLY_TEMPLATE_ROLE_USER = gql`
  mutation ApplyTemplateRoleUser($templateRoleUserId: ID!, $userIds: [ID!]!) {
    applyTemplateRoleUser(
      templateRoleUserId: $templateRoleUserId
      userIds: $userIds
    ) {
      success
    }
  }
`

export const SEND_TEMPLATE_TASK_EMAIL_SAMPLE = gql`
  mutation SendTemplateTaskEmailSample($templateTaskId: ID!) {
    sendTemplateTaskEmailSample(templateTaskId: $templateTaskId) {
      success
      toEmail
    }
  }
`

export const SEND_WELCOME_EMAIL_SAMPLE = gql`
  mutation SendWelcomeEmailSample($type: String!) {
    sendWelcomeEmailSample(type: $type) {
      success
      toEmail
    }
  }
`

export const SEND_CERTIFICATE_AWARD_EMAIL_SAMPLE = gql`
  mutation SendCertificateAwardEmailSample(
    $certificateId: ID!
    $subject: String!
    $body: String
  ) {
    sendCertificateAwardEmailSample(
      certificateId: $certificateId
      subject: $subject
      body: $body
    ) {
      enqueued
      requestToken
      toEmail
    }
  }
`

export const UPDATE_CONTENT_PAGE_SECTIONS_ORDER = gql`
  mutation UpdateContentPageSectionsOrder($contentPageSectionsIds: [ID!]!) {
    updateContentPageSectionsOrder(
      contentPageSectionsIds: $contentPageSectionsIds
    ) {
      id
    }
  }
`

export const UPDATE_CONTENT_PAGE = gql`
  mutation UpdateContentPage($attributes: PageInput!) {
    updatePage(attributes: $attributes) {
      id
    }
  }
`

export const UPDATE_RESOURCE_PAGE_ORDER = gql`
  mutation UpdateResourcePageOrder(
    $sections: [ResourceSectionInputReordering!]!
  ) {
    updateResourcePageOrder(sections: $sections) {
      success
    }
  }
`

export const UPLOAD_ICON_TO_LIBRARY = gql`
  mutation UploadIconToLibrary($attributes: IconInput!) {
    uploadIconToLibrary(attributes: $attributes) {
      id
      name
      url
    }
  }
`

export const DELETE_ICON_LIBRARY = gql`
  mutation DeleteIconLibrary($id: ID!) {
    deleteIconLibrary(id: $id) {
      id
    }
  }
`

export const UPDATE_USER_PREFERENCES = gql`
  mutation UpdateUserPreferences($attributes: UpdateUserPreferencesInput!) {
    updateUserPreferences(attributes: $attributes) {
      preferences {
        showCompletedWorkflow
        showInactiveTemplates
        showEnrollableTemplates
        templateListFilters
        templatePreviewColumns
        templateFolderSidebarOpen
        templatesListColumns
        templateTasksColumns
        templatesListColumnWidths
        templateTasksColumnWidths
        enrollmentsColumns
        reportUserStatisticsColumns
        reportCourseStatisticsColumns
        enrollmentDateFilter
        includeTerminatedEmployees
        includeAutomationTasks
        globalReportUserStatisticsColumns
        globalReportTaskStatisticsColumns
        globalReportFilters
        courseReportCohortDates
        badgesAnalyticsColumnWidths
        badgesAnalyticsColumnVisibility
        badgesAnalyticsColumnOrder
        badgesAnalyticsFilters
        contentHubListFilters
        contentHubListColumns
      }
    }
  }
`

export const ASK_CHAT = gql`
  mutation AskChat($messages: [ChatMessageInput!]!, $type: String) {
    askChat(messages: $messages, type: $type) {
      content
      role
    }
  }
`

export const CREATE_DATE_COHORT = gql`
  mutation CreateDateCohort($attributes: DateCohortInput!) {
    createDateCohort(attributes: $attributes) {
      id
      name
      description
      independent
      segment
      cohortType
      customFieldDefinitions
      dateCohortDates {
        id
        fromDate
        toDate
        date
        name
        customFieldValues
        capacity
        enrolledCount
        availableSlots
      }
      taxonomyTerms {
        id
        title
        taxonomyId
      }
      excludedTaxonomyTerms {
        id
        title
        taxonomyId
      }
    }
  }
`

export const UPDATE_DATE_COHORT = gql`
  mutation UpdateDateCohort($id: ID!, $attributes: DateCohortInput!) {
    updateDateCohort(id: $id, attributes: $attributes) {
      name
      description
      customFieldDefinitions
      dateCohortDates {
        id
        fromDate
        toDate
        date
        name
        blackoutDates
        customFieldValues
        capacity
        enrolledCount
        availableSlots
      }
      taxonomyTerms {
        id
        title
        taxonomyId
      }
      excludedTaxonomyTerms {
        id
        title
        taxonomyId
      }
    }
  }
`

export const UPSERT_COHORT_DATE_BLACKOUT_DATES = gql`
  mutation UpsertCohortDateBlackoutDates(
    $attributes: CohortDateBlackoutDateInput!
  ) {
    upsertCohortDateBlackoutDates(attributes: $attributes) {
      success
    }
  }
`

export const DELETE_DATE_COHORT = gql`
  mutation DeleteDateCohort($id: ID!) {
    deleteDateCohort(id: $id) {
      success
    }
  }
`

export const DUPLICATE_DATE_COHORT = gql`
  mutation DuplicateDateCohort($id: ID!, $name: String) {
    duplicateDateCohort(id: $id, name: $name) {
      id
      name
      description
    }
  }
`

// Reconciles an independent cohort's taxonomy-matched membership. Runs inline on
// the backend (not via the job) so the admin sees what actually changed.
export const SYNC_SEGMENT_MEMBERS = gql`
  mutation SyncSegmentMembers($dateCohortId: ID!) {
    syncSegmentMembers(dateCohortId: $dateCohortId) {
      addedCount
      removedCount
      memberCount
    }
  }
`

// The Create Event audience step's "Upload emails" option: turns a list of
// employee emails into a plain segment the event attaches like any other.
export const CREATE_SEGMENT_FROM_EMAILS = gql`
  mutation CreateSegmentFromEmails($name: String!, $emails: [String!]!) {
    createSegmentFromEmails(name: $name, emails: $emails) {
      dateCohort {
        id
        name
        segment
        description
        memberCount
      }
      unmatchedEmails
    }
  }
`

export const SWITCH_CLIENTS = gql`
  mutation SwitchClients($attributes: SwitchClientsInput!) {
    switchClients(attributes: $attributes) {
      success
    }
  }
`

export const SLACK_UNINSTALL = gql`
  mutation SlackUninstall {
    slackUninstall {
      success
    }
  }
`

export const SEND_EMAIL_TASK = gql`
  mutation SendEmailTask($taskId: String!) {
    sendEmailTask(taskId: $taskId) {
      success
      message
    }
  }
`

export const UPSERT_CONTENT_ITEMS_SECTION = gql`
  mutation UpsertContentItemsSection($attributes: ContentItemsSectionInput!) {
    upsertContentItemsSection(attributes: $attributes) {
      id
      title
      position
      sectionType
      parentId
    }
  }
`

export const UPSERT_CONTENT_ITEM = gql`
  mutation UpsertContentItem($attributes: ContentItemInput!) {
    upsertContentItem(attributes: $attributes) {
      id
      title
      description
      descriptionRaw
      position
      contentItemsSectionId
    }
  }
`

export const BULK_UPSERT_CONTENT_ITEMS = gql`
  mutation BulkUpsertContentItems($attributes: [ContentItemInput!]!) {
    bulkUpsertContentItems(attributes: $attributes) {
      id
      title
      description
      descriptionRaw
      position
      contentItemsSectionId
      contentItemsSection {
        id
        title
      }
    }
  }
`

export const UPLOAD_CONTENT_ITEMS = gql`
  mutation UploadContentItems($attributes: UploadContentItemsInput!) {
    uploadContentItems(attributes: $attributes) {
      id
      title
      description
      position
      contentItemsSectionId
      contentItemsSection {
        id
        title
      }
    }
  }
`

export const DELETE_CONTENT_ITEMS_SECTION = gql`
  mutation DeleteContentItemsSection($id: ID!, $newContentItemsSectionId: ID) {
    deleteContentItemsSection(
      id: $id
      newContentItemsSectionId: $newContentItemsSectionId
    ) {
      success
    }
  }
`

export const UPDATE_CONTENT_ITEMS_SECTIONS_ORDER = gql`
  mutation UpdateContentItemsSectionsOrder(
    $sections: [ContentItemsSectionInputReordering!]!
  ) {
    updateContentItemsSectionsOrder(sections: $sections) {
      success
    }
  }
`

export const DELETE_CONTENT_ITEM = gql`
  mutation DeleteContentItem($id: ID!) {
    deleteContentItem(id: $id) {
      success
    }
  }
`

export const CREATE_QUESTION_SET = gql`
  mutation CreateQuestionSet($attributes: QuestionSetInput!) {
    createQuestionSet(attributes: $attributes) {
      id
      name
      templateTaskType
      position
      contentItemsSectionId
    }
  }
`

export const UPDATE_QUESTION_SET = gql`
  mutation UpdateQuestionSet($id: ID!, $attributes: QuestionSetInput!) {
    updateQuestionSet(id: $id, attributes: $attributes) {
      id
      name
      templateTaskType
      position
      contentItemsSectionId
    }
  }
`

export const DELETE_QUESTION_SET = gql`
  mutation DeleteQuestionSet($id: ID!) {
    deleteQuestionSet(id: $id) {
      success
    }
  }
`

// Question-library section CRUD funnels through the shared
// UPSERT_CONTENT_ITEMS_SECTION / DELETE_CONTENT_ITEMS_SECTION /
// UPDATE_CONTENT_ITEMS_SECTIONS_ORDER mutations. Pass `sectionType`
// (SURVEY / QUIZ / SCORECARD) inside `attributes` to target a question-
// library section; omit it for a legacy Content Items section.

// Zoom Templates tab: like the question-library child CRUD, the section layer
// reuses the shared section mutations (sectionType = ZOOM_TEMPLATE). Upsert
// handles both create and edit; the tool-appropriate option input is folded
// into the preset's stored settings server-side.
export const UPSERT_ZOOM_TEMPLATE_PRESET = gql`
  mutation UpsertZoomTemplatePreset($attributes: ZoomTemplatePresetInput!) {
    upsertZoomTemplatePreset(attributes: $attributes) {
      id
      name
      conferenceTool
      position
      contentItemsSectionId
      settings {
        __typename
        ... on ZoomMeetingOptions {
          security
          registrationRequired
          joinBeforeHost
          autoRecording
          muteUponEntry
          questionAndAnswer
          alternativeHosts
          zoomDescription
          templateId
          thirdPartySurvey
        }
        ... on ZoomWebinarOptions {
          altHosts
          altHostsEditPolls
          authAttendees
          authPanelists
          autoRecord
          emailInAttendeeReport
          hdScreenShare
          onDemand
          practiceSession
          qna
          recordLocation
          registrationRequired
          requirePasscode
          videoHost
          videoPanelists
          zoomDescription
          templateId
          thirdPartySurvey
        }
      }
    }
  }
`

export const DELETE_ZOOM_TEMPLATE_PRESET = gql`
  mutation DeleteZoomTemplatePreset($id: ID!) {
    deleteZoomTemplatePreset(id: $id) {
      success
    }
  }
`

// Content Library "SCORM" tab. Uploading/replacing scormCourseAttachmentId
// (re)runs the SCORM import; omit it for a rename / re-section only.
export const UPSERT_SCORM_LIBRARY_COURSE = gql`
  mutation UpsertScormLibraryCourse($attributes: ScormLibraryCourseInput!) {
    upsertScormLibraryCourse(attributes: $attributes) {
      id
      title
      uploadState
      uploadError
      filename
      contentItemsSectionId
    }
  }
`

export const DELETE_SCORM_LIBRARY_COURSE = gql`
  mutation DeleteScormLibraryCourse($id: ID!) {
    deleteScormLibraryCourse(id: $id) {
      success
    }
  }
`

// Content Library "Video" tab. Providing videoAttachmentId (a signed blob) or
// videoUrl (a direct-video URL) sets/replaces the content; omit both for a
// rename / re-section only. Mirrors UPSERT_SCORM_LIBRARY_COURSE.
export const UPSERT_VIDEO_LIBRARY_VIDEO = gql`
  mutation UpsertVideoLibraryVideo($attributes: VideoLibraryVideoInput!) {
    upsertVideoLibraryVideo(attributes: $attributes) {
      id
      title
      videoUrl
      filename
      contentItemsSectionId
    }
  }
`

export const DELETE_VIDEO_LIBRARY_VIDEO = gql`
  mutation DeleteVideoLibraryVideo($id: ID!) {
    deleteVideoLibraryVideo(id: $id) {
      success
    }
  }
`

// Content Library "AI Roleplay" tab (ENGG-10143). Library metadata only
// (section / name / position, or publish-from-task); the roleplay's content
// is edited through UPSERT_ROLEPLAY.
export const UPSERT_AI_ROLEPLAY_LIBRARY_ITEM = gql`
  mutation UpsertAiRoleplayLibraryItem(
    $attributes: AiRoleplayLibraryItemInput!
  ) {
    upsertAiRoleplayLibraryItem(attributes: $attributes) {
      id
      title: libraryTitle
      name
      description
      position
      contentItemsSectionId
      inContentLibrary
      templateTasksCount
      chatPersonaId
      aiRoleplayTypeId
      questionSetId
      chatPersona {
        id
        name
      }
    }
  }
`

export const DELETE_AI_ROLEPLAY_LIBRARY_ITEM = gql`
  mutation DeleteAiRoleplayLibraryItem($id: ID!) {
    deleteAiRoleplayLibraryItem(id: $id) {
      success
    }
  }
`

// "Make a copy for this task": the task gets its own private copy of the
// Content Library roleplay it was linked to.
export const DETACH_AI_ROLEPLAY_FROM_TEMPLATE_TASK = gql`
  mutation DetachAiRoleplayFromTemplateTask($templateTaskId: ID!) {
    detachAiRoleplayFromTemplateTask(templateTaskId: $templateTaskId) {
      id
      aiRoleplayId
      chatPersonaId
      aiRoleplayTypeId
      questionSetId
      aiRoleplay {
        id
        name
        libraryTitle
        contentItemsSectionId
      }
    }
  }
`

export const CHECK_EMAIL_LIST = gql`
  mutation CheckEmailList($emails: [String!]!) {
    checkEmailList(emails: $emails) {
      matchingUsers {
        id
        fullName
        email
      }
      missingUserEmails
    }
  }
`

export const BULK_ASSIGN_TEMPLATES = gql`
  mutation BulkAssignTemplates(
    $userIds: [ID!]!
    $templateCategoryIds: [ID!]!
    $option: String
    $filters: JSON
    $locationHierarchyIds: [ID!]
  ) {
    bulkAssignTemplates(
      userIds: $userIds
      templateCategoryIds: $templateCategoryIds
      option: $option
      filters: $filters
      locationHierarchyIds: $locationHierarchyIds
    ) {
      success
    }
  }
`

export const CLEAR_BULK_ASSIGN_ERRORS = gql`
  mutation ClearBulkAssignErrors($userId: ID!) {
    clearBulkAssignErrors(userId: $userId) {
      success
    }
  }
`

// ENGG-9187 — one-shot template publish. Backend does the REIMPORT/IMPORT split
// so the frontend no longer needs to make two BULK_ASSIGN_TEMPLATES calls, and
// the selectAll* flags let the backend expand a group without the client having
// to enumerate every user id across pages.
export const PUBLISH_TEMPLATE_CATEGORY_TO_USERS = gql`
  mutation PublishTemplateCategoryToUsers(
    $templateCategoryId: ID!
    $reimportUserIds: [ID!]
    $importUserIds: [ID!]
    $selectAllAssigned: Boolean
    $selectAllEligible: Boolean
    $selectAllNotEligible: Boolean
  ) {
    publishTemplateCategoryToUsers(
      templateCategoryId: $templateCategoryId
      reimportUserIds: $reimportUserIds
      importUserIds: $importUserIds
      selectAllAssigned: $selectAllAssigned
      selectAllEligible: $selectAllEligible
      selectAllNotEligible: $selectAllNotEligible
    ) {
      success
    }
  }
`

// ENGG-9187 — task-publish counterpart. Combines the previous
// REIMPORT_TEMPLATE_TASK + ADD_TEMPLATE_TASK_TO_USERS calls into one round trip.
// Task publish does not surface not-eligible selection, so no
// selectAllNotEligible flag here.
export const PUBLISH_TEMPLATE_TASK_TO_USERS = gql`
  mutation PublishTemplateTaskToUsers(
    $templateTaskId: ID!
    $reimportUserIds: [ID!]
    $addUserIds: [ID!]
    $selectAllAssigned: Boolean
    $selectAllEligible: Boolean
  ) {
    publishTemplateTaskToUsers(
      templateTaskId: $templateTaskId
      reimportUserIds: $reimportUserIds
      addUserIds: $addUserIds
      selectAllAssigned: $selectAllAssigned
      selectAllEligible: $selectAllEligible
    ) {
      success
    }
  }
`

// ENGG-9764 — "Publish all" after a bulk grid edit: runs the task publish
// (reimport assigned + add eligible) for every listed template task.
export const PUBLISH_TEMPLATE_TASKS_TO_EXISTING_WORKFLOWS = gql`
  mutation PublishTemplateTasksToExistingWorkflows($templateTaskIds: [ID!]!) {
    publishTemplateTasksToExistingWorkflows(templateTaskIds: $templateTaskIds) {
      success
      runId
    }
  }
`

export const REIMPORT_TEMPLATE_TASK = gql`
  mutation ReimportTemplateTask($id: ID!, $userIds: [ID!], $taskIds: [ID!]) {
    reimportTemplateTask(id: $id, userIds: $userIds, taskIds: $taskIds) {
      success
      __typename
    }
  }
`

export const RECONCILE_GROUP_EVENTS_ATTENDEES = gql`
  mutation ReconcileGroupEventsAttendees(
    $filters: [ReconcileGroupEventsFilterInput!]!
  ) {
    reconcileGroupEventsAttendees(filters: $filters) {
      success
      __typename
    }
  }
`

export const REVOKE_INTEGRATION_TOKEN = gql`
  mutation RevokeIntegrationToken($type: String!, $owner: String!) {
    revokeIntegrationToken(type: $type, owner: $owner) {
      success
    }
  }
`

export const FAVORITE_INTEGRATION_TOKEN = gql`
  mutation FavoriteIntegrationToken($type: String!, $owner: String!) {
    favoriteIntegrationToken(type: $type, owner: $owner) {
      success
    }
  }
`

export const UPSERT_BLACKOUT_DATES = gql`
  mutation UpsertBlackoutDates($attributes: BlackoutDateInput!) {
    upsertBlackoutDates(attributes: $attributes) {
      success
    }
  }
`

export const UPSERT_CLIENT_BLACKOUT_DATES = gql`
  mutation UpsertClientBlackoutDates($dates: [String!]!) {
    upsertClientBlackoutDates(dates: $dates) {
      success
    }
  }
`

export const UPSERT_TEMPLATE_CATEGORY_BLACKOUT_DATES = gql`
  mutation UpsertTemplateCategoryBlackoutDates(
    $templateCategoryId: ID!
    $dates: [String!]!
  ) {
    upsertTemplateCategoryBlackoutDates(
      templateCategoryId: $templateCategoryId
      dates: $dates
    ) {
      success
    }
  }
`

export const UPDATE_CALENDAR_ADDITIONAL_ACCOUNTS = gql`
  mutation UpdateCalendarAdditionalAccounts($emails: [String!]!) {
    updateCalendarAdditionalAccounts(emails: $emails) {
      email
    }
  }
`

export const UPDATE_GMAIL_ADDITIONAL_ACCOUNTS = gql`
  mutation UpdateGmailAdditionalAccounts($emails: [String!]!) {
    updateGmailAdditionalAccounts(emails: $emails) {
      email
    }
  }
`

export const GET_PRESIGNED_UPLOAD_URL = gql`
  mutation GetPresignedUploadUrl($attributes: PreSignedUrlInput!) {
    getPreSignedUploadUrl(attributes: $attributes) {
      url
      headers
      blobId
      signedBlobId
    }
  }
`

export const UPSERT_TEMPLATE_CATEGORY_FOLDER = gql`
  mutation UpsertTemplateCategoryFolder(
    $attributes: TemplateCategoryFolderInput!
  ) {
    upsertTemplateCategoryFolder(attributes: $attributes) {
      ...TemplateCategoryFolderFields
    }
  }
  ${FRAGMENT_TEMPLATE_CATEGORY_FOLDER}
`

export const DELETE_TEMPLATE_CATEGORY_FOLDER = gql`
  mutation DeleteTemplateCategoryFolder($id: ID!) {
    deleteTemplateCategoryFolder(id: $id) {
      success
    }
  }
`

export const UPDATE_TEMPLATE_CATEGORY_FOLDER_ORDER = gql`
  mutation UpdateTemplateCategoryFoldersOrder(
    $ids: [ID!]!
    $parentFolderId: ID
  ) {
    updateTemplateCategoryFoldersOrder(
      ids: $ids
      parentFolderId: $parentFolderId
    ) {
      ...TemplateCategoryFolderFields
    }
  }
  ${FRAGMENT_TEMPLATE_CATEGORY_FOLDER}
`

export const UPDATE_CLIENT_EMAIL_ICONS = gql`
  mutation UpdateClientEmailIcons($attributes: ClientEmailIconsInput!) {
    updateClientEmailIcons(attributes: $attributes) {
      success
    }
  }
`

export const UPDATE_CLIENT = gql`
  mutation UpdateClient($attributes: ClientAttributesInput!) {
    updateClient(attributes: $attributes) {
      success
    }
  }
`

export const UPDATE_COURSE_IMAGES = gql`
  mutation UpdateCourseImages($attributes: CourseImagesInput!) {
    updateCourseImages(attributes: $attributes) {
      success
    }
  }
`

// ENGG-10027: Settings > Courses switch. ON creates the client's
// show_courses_to_employees config row, OFF removes it.
export const SET_SHOW_COURSES_TO_EMPLOYEES = gql`
  mutation SetShowCoursesToEmployees($enabled: Boolean!) {
    setShowCoursesToEmployees(enabled: $enabled) {
      success
    }
  }
`

// ENGG-10287: Settings > LMS "New course window" — months a course counts as
// New in the employee app (1–24; no saved row reads as 3).
export const SET_NEW_COURSE_WINDOW_MONTHS = gql`
  mutation SetNewCourseWindowMonths($months: Int!) {
    setNewCourseWindowMonths(months: $months) {
      success
    }
  }
`

export const UPSERT_CLIENT_SECRET = gql`
  mutation UpsertClientSecret($attributes: ClientSecretInput!) {
    upsertClientSecret(attributes: $attributes) {
      id
      issuer
      identifier
      secretHint
      data
    }
  }
`

export const UPLOAD_VIDEO_TO_TEMPLATE_TASK = gql`
  mutation UploadVideoToTemplateTask(
    $attributes: UploadVideoToTemplateTaskInput!
  ) {
    uploadVideoToTemplateTask(attributes: $attributes)
  }
`

export const UPLOAD_AUDIO_TO_TEMPLATE_TASK = gql`
  mutation UploadAudioToTemplateTask(
    $attributes: UploadAudioToTemplateTaskInput!
  ) {
    uploadAudioToTemplateTask(attributes: $attributes)
  }
`

export const UPLOAD_SCORM_COURSE_TO_TEMPLATE_TASK = gql`
  mutation UploadScormCourseToTemplateTask(
    $attributes: UploadScormCourseToTemplateTaskInput!
  ) {
    uploadScormCourseToTemplateTask(attributes: $attributes) {
      id
      title
      uploadState
      uploadError
      filename
    }
  }
`

export const UPSERT_TEMPLATE_TASK_OVERRIDE_RULE = gql`
  mutation UpsertTemplateTaskOverrideRules(
    $rules: [TemplateTaskOverrideRuleInput!]!
    $templateTaskId: ID!
  ) {
    upsertTemplateTaskOverrideRules(
      rules: $rules
      templateTaskId: $templateTaskId
    ) {
      id
    }
  }
`

export const UPSERT_REPORT = gql`
  mutation UpsertReport(
    $id: ID
    $name: String!
    $message: String
    $rrule: String
    $oneEmployeePerEmail: Boolean
    $reportDateSetting: TemplateTaskDateSettingInput
    $config: ReportConfigInput
    $folderId: ID
    $filterByTemplateAssignment: Boolean
    $sendAtTime: String
    $sendAtTimezone: String
    $scheduleEndAt: ISO8601DateTime
  ) {
    upsertReport(
      id: $id
      name: $name
      message: $message
      rrule: $rrule
      oneEmployeePerEmail: $oneEmployeePerEmail
      reportDateSetting: $reportDateSetting
      config: $config
      folderId: $folderId
      filterByTemplateAssignment: $filterByTemplateAssignment
      sendAtTime: $sendAtTime
      sendAtTimezone: $sendAtTimezone
      scheduleEndAt: $scheduleEndAt
    ) {
      id
      name
    }
  }
`

export const UPSERT_REPORT_FOLDER = gql`
  mutation UpsertReportFolder($id: ID, $name: String!, $parentId: ID) {
    upsertReportFolder(id: $id, name: $name, parentId: $parentId) {
      id
      name
      parentId
    }
  }
`

export const DELETE_REPORT_FOLDER = gql`
  mutation DeleteReportFolder($id: ID!) {
    deleteReportFolder(id: $id) {
      success
    }
  }
`

export const DELETE_REPORT = gql`
  mutation DeleteReport($id: ID!) {
    deleteReport(id: $id) {
      success
    }
  }
`

export const DUPLICATE_REPORT = gql`
  mutation DuplicateReport($id: ID!, $name: String!) {
    duplicateReport(id: $id, name: $name) {
      id
    }
  }
`

export const GENERATE_REPORT = gql`
  mutation GenerateReport($id: ID!) {
    generateReport(id: $id) {
      id
    }
  }
`

export const GENERATE_REPORTS = gql`
  mutation GenerateReports($ids: [ID!]!) {
    generateReports(ids: $ids) {
      id
    }
  }
`

export const UPDATE_REPORT_VIEW_CONFIG = gql`
  mutation UpdateReportViewConfig(
    $id: ID!
    $viewConfig: ReportViewConfigInput
  ) {
    updateReportViewConfig(id: $id, viewConfig: $viewConfig) {
      id
      name
    }
  }
`

export const CREATE_GROUP_EVENTS = gql`
  mutation CreateGroupEvents(
    $templateCategoryId: ID!
    $dates: [UserDateInput!]!
    $preview: Boolean
    $templateTaskIds: [ID!]
  ) {
    createGroupEvents(
      templateCategoryId: $templateCategoryId
      dates: $dates
      preview: $preview
      templateTaskIds: $templateTaskIds
    ) {
      templateTask {
        id
        title
        internalName
        dueDateType
        position
        templateCategory {
          id
          internalCategory
          position
        }
        calendarEventFacilitators {
          facilitators
          temporaryFacilitators {
            dateRange
            emails
          }
        }
        templateTaskDateSetting {
          time
          timezone
        }
        fixedDueDateDetailed {
          timezone
        }
      }
      dueDate
    }
  }
`

export const DELETE_GROUP_EVENT = gql`
  mutation DeleteGroupEvent($id: ID!) {
    deleteGroupEvent(id: $id) {
      success
    }
  }
`

export const DELETE_GROUP_EVENTS_JOB = gql`
  mutation DeleteGroupEvents($ids: [ID!]!, $block: Boolean) {
    deleteGroupEvents(ids: $ids, block: $block) {
      success
    }
  }
`

// ENGG-10286: the Events tab's "Permanently delete" + "Move its employees to
// the next session": deletes the date for good and moves its employees to
// moveToGroupTaskId (GroupTask.nextSession).
export const SKIP_GROUP_EVENT = gql`
  mutation SkipGroupEvent($id: ID!, $moveToGroupTaskId: ID!) {
    skipGroupEvent(id: $id, moveToGroupTaskId: $moveToGroupTaskId) {
      success
    }
  }
`

export const REIMPORT_GROUP_EVENTS_JOB = gql`
  mutation ReimportGroupEvents($ids: [ID!]!) {
    reimportGroupEvents(ids: $ids) {
      success
    }
  }
`

export const REIMPORT_GROUP_EVENTS_BY_FILTER = gql`
  mutation ReimportGroupEventsByFilter(
    $filters: [ReconcileGroupEventsFilterInput!]!
  ) {
    reimportGroupEventsByFilter(filters: $filters) {
      success
    }
  }
`

export const TRIGGER_USER_IMPORT = gql`
  mutation triggerUserImport($tokenId: ID) {
    triggerUserImport(tokenId: $tokenId) {
      success
    }
  }
`

export const TRIGGER_TAXONOMY_IMPORT = gql`
  mutation triggerTaxonomyImport($tokenId: ID) {
    triggerTaxonomyImport(tokenId: $tokenId) {
      success
    }
  }
`

export const COMPLETE_TASK = gql`
  mutation CompleteTask($taskId: ID!, $userId: ID!) {
    completeTask(taskId: $taskId, userId: $userId) {
      id
      eventType
    }
  }
`

export const CREATE_EVENT = gql`
  mutation CreateEvent($attributes: EventInput!) {
    createEvent(attributes: $attributes) {
      id
      eventType
    }
  }
`

export const CLEAR_CANT_COMPLETE_STATUS = gql`
  mutation ClearCantCompleteStatus($taskId: ID!) {
    clearCantCompleteStatus(taskId: $taskId) {
      id
    }
  }
`

export const SEND_TEMPLATE_TASK_SLACK_SAMPLE = gql`
  mutation SendTemplateTaskSlackSample(
    $templateTaskId: ID!
    $message: String
    $title: String
  ) {
    sendTemplateTaskSlackSample(
      templateTaskId: $templateTaskId
      message: $message
      title: $title
    ) {
      toEmail
      success
    }
  }
`

export const SEND_REPORT_EMAIL = gql`
  mutation SendReportEmail($id: ID!) {
    sendReportEmail(id: $id) {
      success
      message
    }
  }
`

export const SEND_REPORT_EMAIL_TO_ALL_RECIPIENTS = gql`
  mutation SendReportEmailToAllRecipients($id: ID!) {
    sendReportEmailToAllRecipients(id: $id) {
      success
      message
    }
  }
`

export const UPSERT_TEMPLATE_TASK_QUESTION_SECTION = gql`
  mutation UpsertTemplateTaskQuestionSection(
    $id: ID
    $title: String
    $description: String
    $position: Int
    $questionSetId: ID!
  ) {
    upsertTemplateTaskQuestionSection(
      id: $id
      title: $title
      description: $description
      position: $position
      questionSetId: $questionSetId
    ) {
      id
      title
      description
      position
    }
  }
`

export const DELETE_TEMPLATE_TASK_QUESTION_SECTION = gql`
  mutation DeleteTemplateTaskQuestionSection($id: ID!) {
    deleteTemplateTaskQuestionSection(id: $id) {
      success
    }
  }
`

export const SET_DEFAULT_SLACK_USER = gql`
  mutation SetDefaultSlackUser($userId: ID) {
    setDefaultSlackUser(userId: $userId) {
      success
    }
  }
`

export const DELETE_SLACK_USER = gql`
  mutation DeleteSlackUser($userId: ID!) {
    deleteSlackUser(userId: $userId) {
      success
    }
  }
`

export const UPSERT_CUSTOM_FIELD = gql`
  mutation UpsertCustomField($name: String!, $index: Int) {
    upsertCustomField(name: $name, index: $index) {
      customFields {
        key
        name
      }
      errors
    }
  }
`

export const DELETE_CUSTOM_FIELD = gql`
  mutation DeleteCustomField($key: String!) {
    deleteCustomField(key: $key) {
      customFields {
        key
        name
      }
      success
      errors
    }
  }
`

export const DELETE_TEMPLATE_ROLE = gql`
  mutation DeleteTemplateRole($id: ID!) {
    deleteTemplateRole(id: $id) {
      success
      errors
      templateRoles {
        id
        name
      }
    }
  }
`

export const CREATE_AI_ROLEPLAY_FILE = gql`
  mutation CreateAiRoleplayFile($signedBlobId: String!) {
    createAiRoleplayFile(signedBlobId: $signedBlobId) {
      id
      name
      url
      processedTextUrl
      processingStatus
    }
  }
`

export const CREATE_AI_ROLEPLAY_FILE_FROM_TEXT = gql`
  mutation CreateAiRoleplayFileFromText($name: String!, $content: String!) {
    createAiRoleplayFileFromText(name: $name, content: $content) {
      id
      name
      processingStatus
    }
  }
`

export const DESCRIBE_INLINE_FILE = gql`
  mutation DescribeInlineFile($signedBlobId: String!) {
    describeInlineFile(signedBlobId: $signedBlobId) {
      text
      needsConversion
    }
  }
`

export const CREATE_OPENAI_INPUT_FILE = gql`
  mutation CreateOpenaiInputFile(
    $file: Upload
    $aiRoleplayFileId: ID
    $preferProcessedText: Boolean
    $source: String
    $assetId: ID
  ) {
    createOpenaiInputFile(
      file: $file
      aiRoleplayFileId: $aiRoleplayFileId
      preferProcessedText: $preferProcessedText
      source: $source
      assetId: $assetId
    ) {
      fileId
      expiresAt
      contentType
    }
  }
`

export const DELETE_AI_ROLEPLAY_FILE = gql`
  mutation DeleteAiRoleplayFile($id: ID!) {
    deleteAiRoleplayFile(id: $id) {
      success
    }
  }
`

export const UPSERT_ROLEPLAY = gql`
  mutation UpsertRoleplay($attributes: AiRoleplayInput!) {
    upsertRoleplay(attributes: $attributes) {
      id
      clientId
      name
      contentItemsSectionId
      inContentLibrary
      chatPersonaId
      aiRoleplayTypeId
      questionSetId
      chatInstructions
      botOpenerEnabled
      instructions
      guidelines
      successCriteria
      timeLimit
      screenShareEnabled
      subtitlesEnabled
      aiEndedRoleplayEnabled
      personaHandoffEnabled
      personaHandoffCondition
      personaHandoffPrompt
      personaHandoffOverridePrompt
      personaHandoffOpeningLine
      personaHandoffPersona {
        id
        kind
        chatPersonaId
        name
        voice
        role
        organization
        personality
        moodTone
        responseLength
        talkingSpeed
        smallTalk
        englishOnly
        fillerWords
        background
        concerns
        position
        markupLabel
        avatarUrl
      }
      personaPanelEnabled
      personaMode
      personas {
        id
        kind
        chatPersonaId
        name
        voice
        role
        organization
        personality
        moodTone
        responseLength
        talkingSpeed
        smallTalk
        englishOnly
        fillerWords
        background
        concerns
        position
        markupLabel
        avatarUrl
      }
      audioTags {
        tag
        description
      }
      description
      learningObjectives
      displayImageUrl
      published
      tags
      openingLines {
        id
        line
        position
      }
      closingLines {
        id
        line
        definition
        lineType
        position
      }
      keyTopics {
        id
        topic
        description
        successCriteria
        mustCover
        position
      }
      keyTerms {
        id
        term
        definition
        exampleUsage
        pronunciation
        position
      }
      checkpoints {
        id
        timeSeconds
        description
        position
      }
      createdAt
      updatedAt
    }
  }
`

export const PREVIEW_ROLEPLAY_PROMPT = gql`
  mutation PreviewRoleplayPrompt(
    $attributes: AiRoleplayInput!
    $persona: ChatPersonaPreviewInput
    $chatPersonaId: ID
    $aiRoleplayTypeId: ID
    $templateTaskId: ID
  ) {
    previewRoleplayPrompt(
      attributes: $attributes
      persona: $persona
      chatPersonaId: $chatPersonaId
      aiRoleplayTypeId: $aiRoleplayTypeId
      templateTaskId: $templateTaskId
    )
  }
`

export const CREATE_KEY_TERMS_TEMPLATE = gql`
  mutation CreateKeyTermsTemplate($attributes: KeyTermsTemplateInput!) {
    createKeyTermsTemplate(attributes: $attributes) {
      id
      name
      description
      tags
      keyTermsTemplateItems {
        id
        term
        definition
        exampleUsage
        pronunciation
        position
      }
    }
  }
`

export const UPDATE_KEY_TERMS_TEMPLATE = gql`
  mutation UpdateKeyTermsTemplate($attributes: KeyTermsTemplateInput!) {
    updateKeyTermsTemplate(attributes: $attributes) {
      id
      name
      description
      tags
      keyTermsTemplateItems {
        id
        term
        definition
        exampleUsage
        pronunciation
        position
      }
    }
  }
`

export const DELETE_KEY_TERMS_TEMPLATE = gql`
  mutation DeleteKeyTermsTemplate($id: ID!) {
    deleteKeyTermsTemplate(id: $id) {
      id
    }
  }
`

export const UPDATE_CLIENT_PRONUNCIATION_TERMS = gql`
  mutation UpdateClientPronunciationTerms(
    $terms: [ClientPronunciationTermInput!]!
  ) {
    updateClientPronunciationTerms(terms: $terms) {
      id
      term
      pronunciation
      position
    }
  }
`

export const UPDATE_CALENDAR_EVENT = gql`
  mutation UpdateCalendarEvent(
    $groupTaskId: ID!
    $attributes: CalendarEventInput!
  ) {
    updateCalendarEvent(groupTaskId: $groupTaskId, attributes: $attributes) {
      id
      baseDateType {
        id
        name
      }
      baseDateValue
      dueDate
      latestCalendarEvent {
        id
        title
        summary
        attendees {
          email
          name
        }
        start
        startAt
        end
        duration
        organizer
        creator
        timezone
        status
        location
        facilitators
        resources
      }
      templateTask {
        title
        internalName
        calendarEventFacilitators {
          facilitators
          temporaryFacilitators {
            dateRange
            emails
          }
        }
        templateTaskDateSetting {
          time
          timezone
        }
        calendarResources {
          email
          name
        }
      }
    }
  }
`

export const REFRESH_GROUP_EVENT_STATE = gql`
  mutation RefreshGroupEventState($groupTaskId: ID!) {
    refreshGroupEventState(groupTaskId: $groupTaskId) {
      id
      baseDateValue
      latestCalendarEvent {
        id
        title
        summary
        start
        startAt
        end
        duration
        timezone
        facilitators
        attendees {
          email
          name
        }
        status
        lockedFields
      }
    }
  }
`

export const RENAME_SLACK_CHANNEL = gql`
  mutation RenameSlackChannel($channelId: ID!, $name: String!) {
    renameSlackChannel(channelId: $channelId, name: $name) {
      success
    }
  }
`

export const ARCHIVE_SLACK_CHANNEL = gql`
  mutation ArchiveSlackChannel($channelId: ID!) {
    archiveSlackChannel(channelId: $channelId) {
      success
    }
  }
`

export const RETRY_SLACK_GROUP_MESSAGE = gql`
  mutation RetrySlackGroupMessage($slackGroupErrorId: ID!) {
    retrySlackGroupMessage(slackGroupErrorId: $slackGroupErrorId) {
      success
    }
  }
`

export const ADD_USERS_TO_SLACK_CHANNEL = gql`
  mutation AddUsersToSlackChannel($slackGroupId: ID!, $emails: [String!]!) {
    addUsersToSlackChannel(slackGroupId: $slackGroupId, emails: $emails) {
      success
    }
  }
`

export const REMOVE_USERS_FROM_SLACK_CHANNEL = gql`
  mutation RemoveUsersFromSlackChannel(
    $slackGroupId: ID!
    $emails: [String!]!
  ) {
    removeUsersFromSlackChannel(slackGroupId: $slackGroupId, emails: $emails) {
      success
    }
  }
`

export const MOVE_USERS_BETWEEN_SLACK_CHANNELS = gql`
  mutation MoveUsersBetweenSlackChannels(
    $fromSlackGroupId: ID!
    $toSlackGroupId: ID!
    $emails: [String!]!
  ) {
    moveUsersBetweenSlackChannels(
      fromSlackGroupId: $fromSlackGroupId
      toSlackGroupId: $toSlackGroupId
      emails: $emails
    ) {
      success
    }
  }
`

export const CREATE_SCORECARD_REVIEWER_TOKEN = gql`
  mutation CreateScorecardReviewerToken($templateTaskId: ID!) {
    createScorecardReviewerToken(templateTaskId: $templateTaskId) {
      id
      token
      templateTask {
        id
        title
      }
      createdAt
    }
  }
`

export const DELETE_SCORECARD_REVIEWER_TOKEN = gql`
  mutation DeleteScorecardReviewerToken($id: ID!) {
    deleteScorecardReviewerToken(id: $id) {
      id
    }
  }
`

export const SEND_COHORT_EMAIL_PREVIEW = gql`
  mutation SendCohortEmailPreview($cohortEmailId: ID!, $dateCohortDateId: ID!) {
    sendCohortEmailPreview(
      cohortEmailId: $cohortEmailId
      dateCohortDateId: $dateCohortDateId
    ) {
      success
      toEmail
    }
  }
`

export const UPSERT_COHORT_EMAIL = gql`
  mutation UpsertCohortEmail($attributes: CohortEmailInput!) {
    upsertCohortEmail(attributes: $attributes) {
      id
    }
  }
`

export const DELETE_COHORT_EMAIL = gql`
  mutation DeleteCohortEmail($id: ID!) {
    deleteCohortEmail(id: $id) {
      id
      title
      description
    }
  }
`

export const UPSERT_COHORT_SCHEDULE = gql`
  mutation UpsertCohortSchedule($attributes: CohortScheduleInput!) {
    upsertCohortSchedule(attributes: $attributes) {
      ...CohortScheduleFields
    }
  }
  ${FRAGMENT_COHORT_SCHEDULE_FIELDS}
`

export const DELETE_COHORT_SCHEDULE = gql`
  mutation DeleteCohortSchedule($id: ID!) {
    deleteCohortSchedule(id: $id) {
      id
    }
  }
`

// ENGG-8856: manually fire one trigger for one cohort date, bypassing the
// scanner's past-due guard. Returns the recomputed per-date status so the
// Schedule Status modal can refresh that row.
export const FORCE_FIRE_COHORT_SCHEDULE = gql`
  mutation ForceFireCohortSchedule(
    $cohortScheduleId: ID!
    $dateCohortDateId: ID!
  ) {
    forceFireCohortSchedule(
      cohortScheduleId: $cohortScheduleId
      dateCohortDateId: $dateCohortDateId
    ) {
      status
      scheduledAt
      latestFiredAt
      latestError
      forced
      forcedByName
      schedule {
        id
      }
    }
  }
`

export const CREATE_PERSONA = gql`
  mutation CreatePersona(
    $name: String!
    $jobTitle: String
    $company: String
    $voice: String
    $demeanor: String
    $additionalBackground: String
    $responseLength: String
    $smallTalk: Boolean
    $talkingSpeed: String
    $englishOnly: Boolean
    $fillerWords: Boolean
    $concerns: String
    $moodTone: String
    $avatarId: ID
    $tags: [String!]
  ) {
    createChatPersona(
      name: $name
      jobTitle: $jobTitle
      company: $company
      voice: $voice
      demeanor: $demeanor
      additionalBackground: $additionalBackground
      responseLength: $responseLength
      smallTalk: $smallTalk
      talkingSpeed: $talkingSpeed
      englishOnly: $englishOnly
      fillerWords: $fillerWords
      concerns: $concerns
      moodTone: $moodTone
      avatarId: $avatarId
      tags: $tags
    ) {
      chatPersona {
        id
        name
        jobTitle
        company
        voice
        demeanor
        additionalBackground
        moodTone
        tags
      }
      errors
    }
  }
`

export const CREATE_SCORECARD_TEMPLATE = gql`
  mutation CreateScorecardTemplate($attributes: ScorecardTemplateInput!) {
    createScorecardTemplate(attributes: $attributes) {
      id
      name
      description
      templateTaskQuestionSections {
        id
        title
        position
      }
      templateTaskQuestions {
        id
        title
        description
        explanation
        required
        quizType
        order
        templateTaskQuestionSectionId
        options {
          id
          title
          description
          score
          correct
          order
        }
      }
    }
  }
`

export const UPDATE_SCORECARD_TEMPLATE = gql`
  mutation UpdateScorecardTemplate(
    $id: ID!
    $attributes: ScorecardTemplateInput!
  ) {
    updateScorecardTemplate(id: $id, attributes: $attributes) {
      id
      name
      description
      templateTaskQuestionSections {
        id
        title
        position
      }
      templateTaskQuestions {
        id
        title
        description
        explanation
        required
        quizType
        order
        templateTaskQuestionSectionId
        options {
          id
          title
          description
          score
          correct
          order
        }
      }
    }
  }
`

export const CREATE_PREVIEW_TASK_SHARE = gql`
  mutation CreatePreviewTaskShare($input: CreatePreviewTaskShareInput!) {
    createPreviewTaskShare(input: $input) {
      id
      name
      token
      shareUrl
      createdAt
      filters
    }
  }
`

// ENGG-8920 (item 11): saving a View refreshes the share link generated from it,
// so an already-shared URL serves the new filters. The token (and therefore the
// URL) is unchanged.
export const UPDATE_PREVIEW_TASK_SHARE = gql`
  mutation UpdatePreviewTaskShare($id: ID!, $input: UpdatePreviewTaskShareInput!) {
    updatePreviewTaskShare(id: $id, input: $input) {
      id
      name
      token
      shareUrl
      createdAt
      filters
    }
  }
`

export const DELETE_PREVIEW_TASK_SHARE = gql`
  mutation DeletePreviewTaskShare($id: ID!) {
    deletePreviewTaskShare(id: $id) {
      id
      name
      token
      shareUrl
      createdAt
      filters
    }
  }
`

// ENGG-9567: public share link for a template's Reports page. Mirrors the
// preview-task-share (agenda) mutations above.
export const CREATE_TEMPLATE_REPORT_SHARE = gql`
  mutation CreateTemplateReportShare($input: CreateTemplateReportShareInput!) {
    createTemplateReportShare(input: $input) {
      id
      token
      shareUrl
      createdAt
      filters
    }
  }
`

export const DELETE_TEMPLATE_REPORT_SHARE = gql`
  mutation DeleteTemplateReportShare($id: ID!) {
    deleteTemplateReportShare(id: $id) {
      id
      token
      shareUrl
      createdAt
      filters
    }
  }
`

export const UPDATE_PROMPTS = gql`
  mutation UpdatePrompts($prompts: [PromptInput!]!) {
    updatePrompts(prompts: $prompts) {
      category
      subcategories {
        subcategory
        prompt
      }
    }
  }
`

export const CREATE_SFTP_USER = gql`
  mutation CreateSftpUser {
    createSftpUser {
      success
      host
      username
      password
      homeDirectory
      alias
      error
    }
  }
`

export const GENERATE_SFTP_PGP_KEY = gql`
  mutation GenerateSftpPgpKey {
    generateSftpPgpKey {
      success
      error
      key {
        id
        publicKey
        fingerprint
        displayFingerprint
        keyId
        uid
        revokedAt
        createdAt
      }
    }
  }
`

export const REVOKE_SFTP_PGP_KEY = gql`
  mutation RevokeSftpPgpKey($id: ID!) {
    revokeSftpPgpKey(id: $id) {
      success
      error
      key {
        id
        revokedAt
      }
    }
  }
`

export const CREATE_REPORT_AI_CHAT_SESSION = gql`
  mutation CreateReportAiChatSession(
    $reportId: ID!
    $prompt: String!
    $sessionName: String
    $fileUrls: [String!]
  ) {
    createReportAiChatSession(
      reportId: $reportId
      prompt: $prompt
      sessionName: $sessionName
      fileUrls: $fileUrls
    ) {
      id
      reportId
      name
      prompt
      status
      messages {
        id
        role
        content
        fileUrls
        fileNames
        createdAt
      }
      createdAt
    }
  }
`

export const DELETE_REPORT_AI_CHAT_SESSION = gql`
  mutation DeleteReportAiChatSession($id: ID!) {
    deleteReportAiChatSession(id: $id) {
      success
      id
    }
  }
`

export const CREATE_REPORT_AI_SUGGESTION = gql`
  mutation CreateReportAiSuggestion($reportId: ID!, $content: String!) {
    createReportAiSuggestion(reportId: $reportId, content: $content) {
      id
      content
      position
      createdAt
      updatedAt
    }
  }
`

export const UPDATE_REPORT_AI_SUGGESTION = gql`
  mutation UpdateReportAiSuggestion($id: ID!, $content: String!) {
    updateReportAiSuggestion(id: $id, content: $content) {
      id
      content
      position
      createdAt
      updatedAt
    }
  }
`

export const DELETE_REPORT_AI_SUGGESTION = gql`
  mutation DeleteReportAiSuggestion($id: ID!) {
    deleteReportAiSuggestion(id: $id) {
      success
      id
    }
  }
`

export const CONNECT_CONNECTOR = gql`
  mutation ConnectConnector($providerKey: String!, $credentials: JSON!) {
    connectConnector(providerKey: $providerKey, credentials: $credentials) {
      success
      error
      connectedAt
    }
  }
`

export const SET_CONNECTOR_DATASETS = gql`
  mutation SetConnectorDatasets(
    $providerKey: String!
    $datasetIds: [String!]!
  ) {
    setConnectorDatasets(providerKey: $providerKey, datasetIds: $datasetIds) {
      success
    }
  }
`

export const SYNC_CONNECTOR = gql`
  mutation SyncConnector($providerKey: String) {
    syncConnector(providerKey: $providerKey) {
      success
      error
      results {
        providerKey
        datasetId
        filePath
        error
      }
    }
  }
`

export const UPDATE_SFTP_FILE_PROCESSING = gql`
  mutation UpdateSftpFileProcessing(
    $filePath: String!
    $enabled: Boolean!
    $terminateOrRescindMissingEmployees: Boolean
    $updateExistingEmployeesOnly: Boolean
  ) {
    updateSftpFileProcessing(
      filePath: $filePath
      enabled: $enabled
      terminateOrRescindMissingEmployees: $terminateOrRescindMissingEmployees
      updateExistingEmployeesOnly: $updateExistingEmployeesOnly
    ) {
      success
    }
  }
`

export const UPDATE_SFTP_HEADER_MAPPINGS = gql`
  mutation UpdateSftpHeaderMappings(
    $filePath: String!
    $mappings: [SFTPHeaderMappingInput!]!
    $compositeMappings: [SFTPCompositeMappingInput!]
    $importFilters: [SFTPImportFilterInput!]
  ) {
    updateSftpHeaderMappings(
      filePath: $filePath
      mappings: $mappings
      compositeMappings: $compositeMappings
      importFilters: $importFilters
    ) {
      success
    }
  }
`

export const GENERATE_DOWNLOAD_URL = gql`
  mutation GenerateDownloadUrl($chatSessionId: ID!, $downloadType: String!) {
    generateDownloadUrl(
      chatSessionId: $chatSessionId
      downloadType: $downloadType
    ) {
      downloadUrl
      errors
    }
  }
`

export const CANCEL_BULK_ASSIGN_TEMPLATE = gql`
  mutation CancelBulkAssignTemplate($id: ID!) {
    cancelBulkAssignTemplate(id: $id) {
      success
    }
  }
`

export const BULK_ASSIGN_CERTIFICATIONS = gql`
  mutation BulkAssignCertifications(
    $userIds: [ID!]!
    $certificateIds: [ID!]!
    $issuedAt: ISO8601DateTime!
    $filters: JSON
  ) {
    bulkAssignCertifications(
      userIds: $userIds
      certificateIds: $certificateIds
      issuedAt: $issuedAt
      filters: $filters
    ) {
      success
    }
  }
`

// ENGG-8989 — bulk assign one or more standalone badges to many users
// at a shared issued_at. Sibling of BULK_ASSIGN_CERTIFICATIONS; the
// backend creates one UserBadge per (user, badge) pair via
// ManualBadgeIssuer.issue_badge_only. Already-held badges (via
// UserCertification or a prior UserBadge) are silently skipped and
// surfaced through the bulk history + BulkAssignBadgeChannel updates.
export const BULK_ASSIGN_BADGES = gql`
  mutation BulkAssignBadges(
    $userIds: [ID!]!
    $badgeIds: [ID!]!
    $issuedAt: ISO8601DateTime!
    $filters: JSON
  ) {
    bulkAssignBadges(
      userIds: $userIds
      badgeIds: $badgeIds
      issuedAt: $issuedAt
      filters: $filters
    ) {
      success
    }
  }
`

export const CANCEL_BULK_ASSIGN_CERTIFICATION = gql`
  mutation CancelBulkAssignCertification($id: ID!) {
    cancelBulkAssignCertification(id: $id) {
      success
    }
  }
`

export const CANCEL_BULK_ASSIGN_BADGE = gql`
  mutation CancelBulkAssignBadge($id: ID!) {
    cancelBulkAssignBadge(id: $id) {
      success
    }
  }
`

export const UPSERT_REPORT_AI_PROMPT = gql`
  mutation UpsertReportAiPrompt($promptType: String!, $content: String!) {
    upsertReportAiPrompt(promptType: $promptType, content: $content) {
      id
      promptType
      content
    }
  }
`

export const UPDATE_SUPER_ADMIN_REPORT_NOTIFICATION = gql`
  mutation UpdateSuperAdminReportNotification(
    $reportId: ID!
    $userId: ID!
    $enabled: Boolean!
  ) {
    updateSuperAdminReportNotification(
      reportId: $reportId
      userId: $userId
      enabled: $enabled
    ) {
      id
      title
      description
      frequency
      disabledUserIds
      fixedRecipients
    }
  }
`

export const UPDATE_IP_ALLOWLIST = gql`
  mutation UpdateIpAllowlist($ipAllowlist: [String!]!) {
    updateIpAllowlist(ipAllowlist: $ipAllowlist)
  }
`

export const CREATE_AI_ASSISTANT_SESSION = gql`
  mutation CreateAiAssistantSession($title: String, $model: String) {
    createAiAssistantSession(title: $title, model: $model) {
      session {
        id
        title
        status
        model
        createdAt
      }
      errors
    }
  }
`

export const ARCHIVE_AI_ASSISTANT_SESSION = gql`
  mutation ArchiveAiAssistantSession($id: ID!) {
    archiveAiAssistantSession(id: $id) {
      session {
        id
        status
      }
      errors
    }
  }
`

export const SET_AI_ASSISTANT_BRAIN_ENABLED = gql`
  mutation SetAiAssistantBrainEnabled($id: ID!, $enabled: Boolean!) {
    setAiAssistantBrainEnabled(id: $id, enabled: $enabled) {
      session {
        id
        brainEnabled
      }
      errors
    }
  }
`

export const CREATE_ACTION_TARGET = gql`
  mutation CreateActionTarget(
    $sessionId: ID!
    $targetType: String!
    $targetId: ID!
  ) {
    createAiAssistantActionTarget(
      sessionId: $sessionId
      targetType: $targetType
      targetId: $targetId
    ) {
      actionTarget {
        id
        targetType
        targetId
        status
        pendingChanges
        originalSnapshot
        createdAt
        updatedAt
      }
      errors
    }
  }
`

export const DISCARD_ACTION_TARGET = gql`
  mutation DiscardActionTarget($id: ID!) {
    discardAiAssistantActionTarget(id: $id) {
      actionTarget {
        id
        status
      }
      errors
    }
  }
`

export const APPLY_ACTION_TARGET = gql`
  mutation ApplyActionTarget(
    $id: ID!
    $acceptedChangeIds: [ID!]
    $force: Boolean
    $folderId: ID
  ) {
    applyAiAssistantActionTarget(
      id: $id
      acceptedChangeIds: $acceptedChangeIds
      force: $force
      folderId: $folderId
    ) {
      results
      errors
    }
  }
`

export const PUBLISH_AS_NEW_TEMPLATE = gql`
  mutation PublishAsNewTemplate(
    $actionTargetId: ID!
    $title: String
    $folderId: ID
  ) {
    publishAsNewTemplate(
      actionTargetId: $actionTargetId
      title: $title
      folderId: $folderId
    ) {
      templateCategory {
        id
        category
      }
      errors
    }
  }
`

export const UPDATE_ACTION_TARGET_CHANGES = gql`
  mutation UpdateActionTargetChanges($id: ID!, $pendingChanges: JSON!) {
    updateAiAssistantActionTargetChanges(
      id: $id
      pendingChanges: $pendingChanges
    ) {
      actionTarget {
        id
        pendingChanges
      }
      errors
    }
  }
`

export const REFRESH_ACTION_TARGET_SNAPSHOT = gql`
  mutation RefreshActionTargetSnapshot($id: ID!) {
    refreshAiAssistantActionTargetSnapshot(id: $id) {
      actionTarget {
        id
        originalSnapshot
        pendingChanges
      }
      errors
    }
  }
`

export const CREATE_COURSE_SKILL = gql`
  mutation CreateCourseSkill($attributes: CourseSkillInput!) {
    createCourseSkill(attributes: $attributes) {
      id
      name
    }
  }
`

export const UPDATE_COURSE_SKILL = gql`
  mutation UpdateCourseSkill($id: ID!, $attributes: CourseSkillInput!) {
    updateCourseSkill(id: $id, attributes: $attributes) {
      id
      name
    }
  }
`

export const DELETE_COURSE_SKILL = gql`
  mutation DeleteCourseSkill($id: ID!) {
    deleteCourseSkill(id: $id) {
      id
      name
    }
  }
`

export const CREATE_COURSE_CATEGORY = gql`
  mutation CreateCourseCategory($attributes: CourseCategoryInput!) {
    createCourseCategory(attributes: $attributes) {
      id
      name
    }
  }
`

export const UPDATE_COURSE_CATEGORY = gql`
  mutation UpdateCourseCategory($id: ID!, $attributes: CourseCategoryInput!) {
    updateCourseCategory(id: $id, attributes: $attributes) {
      id
      name
    }
  }
`

export const DELETE_COURSE_CATEGORY = gql`
  mutation DeleteCourseCategory($id: ID!) {
    deleteCourseCategory(id: $id) {
      id
      name
    }
  }
`

export const CREATE_COURSE_FORMAT = gql`
  mutation CreateCourseFormat($attributes: CourseFormatInput!) {
    createCourseFormat(attributes: $attributes) {
      id
      name
    }
  }
`

export const UPDATE_COURSE_FORMAT = gql`
  mutation UpdateCourseFormat($id: ID!, $attributes: CourseFormatInput!) {
    updateCourseFormat(id: $id, attributes: $attributes) {
      id
      name
    }
  }
`

export const DELETE_COURSE_FORMAT = gql`
  mutation DeleteCourseFormat($id: ID!) {
    deleteCourseFormat(id: $id) {
      id
      name
    }
  }
`

export const CREATE_COURSE_LEVEL = gql`
  mutation CreateCourseLevel($attributes: CourseLevelInput!) {
    createCourseLevel(attributes: $attributes) {
      id
      name
    }
  }
`

export const UPDATE_COURSE_LEVEL = gql`
  mutation UpdateCourseLevel($id: ID!, $attributes: CourseLevelInput!) {
    updateCourseLevel(id: $id, attributes: $attributes) {
      id
      name
    }
  }
`

export const DELETE_COURSE_LEVEL = gql`
  mutation DeleteCourseLevel($id: ID!) {
    deleteCourseLevel(id: $id) {
      id
      name
    }
  }
`

export const CREATE_COURSE_CONTENT_TYPE = gql`
  mutation CreateCourseContentType($attributes: CourseContentTypeInput!) {
    createCourseContentType(attributes: $attributes) {
      id
      name
    }
  }
`

export const UPDATE_COURSE_CONTENT_TYPE = gql`
  mutation UpdateCourseContentType(
    $id: ID!
    $attributes: CourseContentTypeInput!
  ) {
    updateCourseContentType(id: $id, attributes: $attributes) {
      id
      name
    }
  }
`

export const DELETE_COURSE_CONTENT_TYPE = gql`
  mutation DeleteCourseContentType($id: ID!) {
    deleteCourseContentType(id: $id) {
      id
      name
    }
  }
`

export const GENERATE_OPENAI_KEY = gql`
  mutation GenerateOpenaiKey(
    $instructions: String!
    $voice: String!
    $aiRoleplayTypeId: ID
    $tools: JSON
  ) {
    generateOpenaiKey(
      instructions: $instructions
      voice: $voice
      aiRoleplayTypeId: $aiRoleplayTypeId
      tools: $tools
    )
  }
`

// screenShareEnabled drives SCREEN_SHARE_INSTRUCTIONS_ADDENDUM on the agent
// prompt, so the AI knows it may be shown a screen (ENGG-8889). Previews used to
// omit the argument entirely, which silently defaulted it to false.
export const CREATE_ELEVENLABS_AGENT = gql`
  mutation CreateElevenlabsAgent(
    $instructions: String
    $voice: String
    $aiRoleplayTypeId: ID
    $aiRoleplayId: ID
    $tools: JSON
    $firstMessage: String
    $timeLimitSeconds: Int
    $screenShareEnabled: Boolean
  ) {
    createElevenlabsAgent(
      instructions: $instructions
      voice: $voice
      aiRoleplayTypeId: $aiRoleplayTypeId
      aiRoleplayId: $aiRoleplayId
      tools: $tools
      firstMessage: $firstMessage
      timeLimitSeconds: $timeLimitSeconds
      screenShareEnabled: $screenShareEnabled
    )
  }
`

export const DELETE_ELEVENLABS_AGENT = gql`
  mutation DeleteElevenlabsAgent($agentId: String!) {
    deleteElevenlabsAgent(agentId: $agentId)
  }
`

// Mirrors team-app-frontend/src/graphql/roleplay-mutations.ts DESCRIBE_SCREEN_FRAME.
// Server-side vision for a screen-share frame; the description is pushed to the
// ElevenLabs agent as a contextual update so it can react to the shared screen.
export const DESCRIBE_SCREEN_FRAME = gql`
  mutation DescribeScreenFrame($fileData: String!, $verbosity: String) {
    describeScreenFrame(fileData: $fileData, verbosity: $verbosity)
  }
`

// Grades an admin preview transcript against the task's real scorecard questions
// and returns the result in memory. Persists nothing, so a preview never appears
// in analytics or on an employee's record (ENGG-8889).
//
// The selection mirrors GET_SCORECARD_CHAT_SESSION in
// team-app-frontend/src/graphql/roleplay-queries.ts so the preview scorecard
// renders the same verdict, coaching moments, analytics and rubric sections the
// employee scorecard does. If you edit one, edit the other.
export const PREVIEW_ROLEPLAY_SCORECARD = gql`
  mutation PreviewRoleplayScorecard(
    $templateTaskId: ID!
    $transcription: JSON!
    $draftQuestions: [QuestionInput!]
    $draftSections: [DraftScorecardSectionInput!]
    $durationSeconds: Int
  ) {
    previewRoleplayScorecard(
      templateTaskId: $templateTaskId
      transcription: $transcription
      draftQuestions: $draftQuestions
      draftSections: $draftSections
      durationSeconds: $durationSeconds
    ) {
      score
      totalScore
      percentage
      passingScore
      chatMaxScore
      feedback
      # Scorecard v3 Overall Score verdict — one or two sentences from the
      # scoring model. Null when the model returned none.
      overallSummary
      # Scorecard v3 Overall pills and Coaching moments. Null when the grading
      # schema never asked for them, [] when it did and found none.
      highlights {
        title
        description
        quote
        recommendation
        timerValue
      }
      improvements {
        title
        description
        quote
        recommendation
        timerValue
      }
      # Scorecard v3 Analytics. Null groups mean "couldn't be measured" —
      # render an empty state, never zeros.
      conversationMetrics {
        turnCount
        userTurnCount
        wordsPerMinute {
          average
          slowest {
            wpm
            timerValue
          }
          fastest {
            wpm
            timerValue
          }
        }
        fillerWords {
          total
          byWord {
            word
            count
            timerValues
          }
        }
        sentenceLength {
          averageWords
          sentenceCount
        }
        talkListen {
          userPct
          aiPct
          userSeconds
          aiSeconds
        }
        longestMonologue {
          seconds
          timerValue
        }
      }
      questions {
        templateTaskQuestionId
        titleSnapshot
        descriptionSnapshot
        orderSnapshot
        templateTaskQuestionSectionId
        templateTaskQuestionSection {
          id
          title
          position
        }
        answers {
          templateTaskQuestionOptionId
          titleSnapshot
          descriptionSnapshot
          scoreSnapshot
          answer
          comment
          recommendation
        }
      }
    }
  }
`

export const IMPORT_DATE_COHORT_DATES = gql`
  mutation ImportDateCohortDates(
    $dateCohortId: ID!
    $csvRows: [DateCohortDateCsvRowInput!]!
  ) {
    importDateCohortDates(dateCohortId: $dateCohortId, csvRows: $csvRows) {
      id
      name
      dateCohortDates {
        id
        date
        fromDate
        toDate
        name
        blackoutDates
      }
    }
  }
`

export const IMPORT_ALL_DATE_COHORT_DATES = gql`
  mutation ImportAllDateCohortDates($csvRows: [DateCohortDateCsvRowInput!]!) {
    importAllDateCohortDates(csvRows: $csvRows) {
      id
      name
      dateCohortDates {
        id
        date
        fromDate
        toDate
        name
        blackoutDates
        customFieldValues
      }
    }
  }
`
export const UPSERT_AWARD_CONDITION = gql`
  mutation UpsertAwardCondition(
    $id: ID
    $name: String!
    $active: Boolean
    $grants: [AwardConditionGrantInput!]!
    $targets: [AwardConditionTargetInput!]!
    $kind: String
    $taskTypeQuotas: [AwardConditionTaskTypeQuotaInput!]
    $badgeIds: [ID!]
    $countBadgesFrom: ISO8601DateTime
  ) {
    upsertAwardCondition(
      id: $id
      name: $name
      active: $active
      grants: $grants
      targets: $targets
      kind: $kind
      taskTypeQuotas: $taskTypeQuotas
      badgeIds: $badgeIds
      countBadgesFrom: $countBadgesFrom
    ) {
      ...AwardConditionFields
    }
  }
  ${FRAGMENT_AWARD_CONDITION_FIELDS}
`

// Soft delete. Credentials already issued under the condition keep pointing at
// it — an employee never loses a badge because an admin tidied up a rule.
export const DELETE_AWARD_CONDITION = gql`
  mutation DeleteAwardCondition($id: ID!) {
    deleteAwardCondition(id: $id) {
      success
    }
  }
`
// ENGG-10101 — issue the awards picked in "Find missing earners", in the
// background, each dated to when the employee met the rule. From a badge or
// certificate card, its id limits each rule to that one award.
export const ASSIGN_MISSING_AWARDS = gql`
  mutation AssignMissingAwards(
    $assignments: [MissingAwardAssignmentInput!]!
    $certificateId: ID
    $badgeId: ID
  ) {
    assignMissingAwards(
      assignments: $assignments
      certificateId: $certificateId
      badgeId: $badgeId
    ) {
      success
    }
  }
`
export const CREATE_BADGE = gql`
  mutation CreateBadge($attributes: BadgeInput!) {
    createBadge(attributes: $attributes) {
      ...BadgeFields
    }
  }
  ${FRAGMENT_BADGE_FIELDS}
`

export const UPDATE_BADGE = gql`
  mutation UpdateBadge($id: ID!, $attributes: BadgeInput!) {
    updateBadge(id: $id, attributes: $attributes) {
      ...BadgeFields
    }
  }
  ${FRAGMENT_BADGE_FIELDS}
`

export const DELETE_BADGE = gql`
  mutation DeleteBadge($id: ID!) {
    deleteBadge(id: $id) {
      id
    }
  }
`

export const DUPLICATE_BADGE = gql`
  mutation DuplicateBadge($id: ID!, $title: String) {
    duplicateBadge(id: $id, title: $title) {
      ...BadgeFields
    }
  }
  ${FRAGMENT_BADGE_FIELDS}
`

// Rewrites the full badge display order (ENGG-9583). `orderedIds` is the
// complete list of badge ids in their new order, as produced by the Content
// Library drag-and-drop list. Returns the reordered badges so the Apollo cache
// picks up the new `position` values.
export const REORDER_BADGES = gql`
  mutation ReorderBadges($orderedIds: [ID!]!) {
    reorderBadges(orderedIds: $orderedIds) {
      ...BadgeFields
    }
  }
  ${FRAGMENT_BADGE_FIELDS}
`

export const ENQUEUE_BADGE_DOWNLOAD = gql`
  mutation EnqueueBadgeDownload(
    $badgeId: ID!
    $svg: String!
    $format: DownloadFormat!
    $filename: String
  ) {
    enqueueBadgeDownload(
      badgeId: $badgeId
      svg: $svg
      format: $format
      filename: $filename
    ) {
      requestToken
    }
  }
`

export const CREATE_CERTIFICATE = gql`
  mutation CreateCertificate($attributes: CertificateInput!) {
    createCertificate(attributes: $attributes) {
      ...CertificateFields
    }
  }
  ${FRAGMENT_CERTIFICATE_FIELDS}
`

export const UPDATE_CERTIFICATE = gql`
  mutation UpdateCertificate($id: ID!, $attributes: CertificateInput!) {
    updateCertificate(id: $id, attributes: $attributes) {
      ...CertificateFields
    }
  }
  ${FRAGMENT_CERTIFICATE_FIELDS}
`

export const DELETE_CERTIFICATE = gql`
  mutation DeleteCertificate($id: ID!) {
    deleteCertificate(id: $id) {
      id
    }
  }
`

export const DUPLICATE_CERTIFICATE = gql`
  mutation DuplicateCertificate($id: ID!, $courseName: String) {
    duplicateCertificate(id: $id, courseName: $courseName) {
      ...CertificateFields
    }
  }
  ${FRAGMENT_CERTIFICATE_FIELDS}
`

// Sibling of REORDER_BADGES — rewrites the full certificate display order.
export const REORDER_CERTIFICATES = gql`
  mutation ReorderCertificates($orderedIds: [ID!]!) {
    reorderCertificates(orderedIds: $orderedIds) {
      ...CertificateFields
    }
  }
  ${FRAGMENT_CERTIFICATE_FIELDS}
`

export const ENQUEUE_CERTIFICATE_DOWNLOAD = gql`
  mutation EnqueueCertificateDownload(
    $certificateId: ID!
    $svg: String!
    $format: DownloadFormat!
    $filename: String
  ) {
    enqueueCertificateDownload(
      certificateId: $certificateId
      svg: $svg
      format: $format
      filename: $filename
    ) {
      requestToken
    }
  }
`

// =====================================================================
// Roleplay Prompt Optimizer
// =====================================================================

export const AI_ROLEPLAY_OPTIMIZATION_FIELDS = gql`
  fragment AiRoleplayOptimizationFields on AiRoleplayOptimization {
    id
    templateTaskId
    status
    kind
    criterionId
    focusNote
    originalInstructions
    proposedInstructions
    summaryOfChanges
    finalScores
    iterations
    traineeScripts
    modelVersions
    promptTokens
    completionTokens
    totalTokens
    errorMessage
    appliedAt
    discardedAt
    createdAt
    updatedAt
    currentStage
    currentIteration
    stageDetail
    stageUpdatedAt
    sources {
      id
      sourceableType
      sourceableId
      label
      createdAt
    }
    createdByUser {
      id
      firstName
      lastName
    }
  }
`

export const CREATE_AI_ROLEPLAY_OPTIMIZATION = gql`
  ${AI_ROLEPLAY_OPTIMIZATION_FIELDS}
  mutation CreateAiRoleplayOptimization(
    $templateTaskId: ID!
    $transcriptFileIds: [ID!]
    $chatSessionIds: [ID!]
    $focusNote: String
  ) {
    createAiRoleplayOptimization(
      templateTaskId: $templateTaskId
      transcriptFileIds: $transcriptFileIds
      chatSessionIds: $chatSessionIds
      focusNote: $focusNote
    ) {
      ...AiRoleplayOptimizationFields
    }
  }
`

// Test Lab: draft a prompt that fixes the failures in a finished criteria
// test set. Returns the pending optimization; poll
// GET_AI_ROLEPLAY_TEST_LAB_RECOMMENDATION for the result.
// Test Lab: turn pasted notes (a comma list, bullets, review prose) into the
// criteria list. Returns plain strings the admin edits before planning.
export const EXTRACT_AI_ROLEPLAY_TEST_CRITERIA = gql`
  mutation ExtractAiRoleplayTestCriteria($templateTaskId: ID!, $text: String!) {
    extractAiRoleplayTestCriteria(templateTaskId: $templateTaskId, text: $text)
  }
`

export const RECOMMEND_AI_ROLEPLAY_PROMPT_FROM_TESTS = gql`
  ${AI_ROLEPLAY_OPTIMIZATION_FIELDS}
  mutation RecommendAiRoleplayPromptFromTests(
    $templateTaskId: ID!
    $batchKey: ID!
    $criterionId: ID
  ) {
    recommendAiRoleplayPromptFromTests(
      templateTaskId: $templateTaskId
      batchKey: $batchKey
      criterionId: $criterionId
    ) {
      ...AiRoleplayOptimizationFields
    }
  }
`

export const APPLY_AI_ROLEPLAY_OPTIMIZATION = gql`
  ${AI_ROLEPLAY_OPTIMIZATION_FIELDS}
  mutation ApplyAiRoleplayOptimization($optimizationId: ID!) {
    applyAiRoleplayOptimization(optimizationId: $optimizationId) {
      ...AiRoleplayOptimizationFields
    }
  }
`

export const DISCARD_AI_ROLEPLAY_OPTIMIZATION = gql`
  ${AI_ROLEPLAY_OPTIMIZATION_FIELDS}
  mutation DiscardAiRoleplayOptimization($optimizationId: ID!) {
    discardAiRoleplayOptimization(optimizationId: $optimizationId) {
      ...AiRoleplayOptimizationFields
    }
  }
`

export const REGENERATE_OPTIMIZED_INSTRUCTIONS = gql`
  mutation RegenerateOptimizedInstructions($templateTaskId: ID!) {
    regenerateOptimizedInstructions(templateTaskId: $templateTaskId) {
      id
      optimizedInstructions
      optimizedInstructionsUpdatedAt
      optimizedInstructionsStale
    }
  }
`

export const ENQUEUE_CERTIFICATE_PREVIEW = gql`
  mutation EnqueueCertificatePreview(
    $id: ID!
    $recipientName: String
    $templateCategoryId: ID
  ) {
    enqueueCertificatePreview(
      id: $id
      recipientName: $recipientName
      templateCategoryId: $templateCategoryId
    ) {
      requestToken
    }
  }
`

export const ADMIN_CLEANUP_APPLY = gql`
  mutation AdminCleanupApply(
    $email: String!
    $replacementEmail: String!
    $actorEmail: String
  ) {
    adminCleanupApply(
      email: $email
      replacementEmail: $replacementEmail
      actorEmail: $actorEmail
    ) {
      findings {
        key
        count
      }
      counters {
        key
        count
      }
      warnings
      dryRun
      actorEmail
    }
  }
`

// Restores a soft-deleted workflow (and all of its sections, tasks, assignments
// and completion history) for an employee. Admin-only on the backend.
export const RESTORE_WORKFLOW = gql`
  mutation RestoreWorkflow($locationHierarchyId: ID!) {
    restoreWorkflow(locationHierarchyId: $locationHierarchyId) {
      success
    }
  }
`

// Manually assigns one or more certificates (badges) to a user at a shared
// issuedAt (ENGG-8931). The backend creates a UserCertification with
// manual=true per cert outside the task-completion pipeline. issuedAt may
// be past, today, or future — future-dated rows surface with
// `scheduled: true` so the UI can label them as scheduled. Certificates
// the user already has (manual or auto-issued) are silently skipped and
// returned as skippedCertificateIds so the UI can surface a partial-skip
// message.
// Cert-assignment mutation. Renamed from ASSIGN_BADGES_TO_USER in
// ENGG-8989 — the old name was misleading because the operation creates
// UserCertification rows, not bare badges. The sibling ASSIGN_BADGES_TO_USER
// below now covers actual badge-only assignment.
export const ASSIGN_CERTIFICATES_TO_USER = gql`
  mutation AssignCertificatesToUser(
    $userId: ID!
    $certificateIds: [ID!]!
    $issuedAt: ISO8601DateTime!
  ) {
    assignCertificatesToUser(
      userId: $userId
      certificateIds: $certificateIds
      issuedAt: $issuedAt
    ) {
      assigned {
        ...AssignedBadgeFields
      }
      skippedCertificateIds
    }
  }
  ${FRAGMENT_ASSIGNED_BADGE_FIELDS}
`

// Badge-only assignment (ENGG-8989) — creates UserBadge rows with no
// accompanying certificate. Badges the user already holds via
// UserCertification or a prior UserBadge are silently skipped.
export const ASSIGN_BADGES_TO_USER = gql`
  mutation AssignBadgesToUser(
    $userId: ID!
    $badgeIds: [ID!]!
    $issuedAt: ISO8601DateTime!
  ) {
    assignBadgesToUser(
      userId: $userId
      badgeIds: $badgeIds
      issuedAt: $issuedAt
    ) {
      assigned {
        ...UserBadgeFields
      }
      skippedBadgeIds
    }
  }
  ${FRAGMENT_USER_BADGE_FIELDS}
`

// Cert removal — renamed from REMOVE_USER_BADGE in ENGG-8989 to match
// the backend rename (Mutations::RemoveUserCertification). The sibling
// REMOVE_USER_BADGE below removes a bare UserBadge row.
export const REMOVE_USER_CERTIFICATION = gql`
  mutation RemoveUserCertification($id: ID!) {
    removeUserCertification(id: $id) {
      id
    }
  }
`

// Removes a manually-awarded UserBadge (ENGG-8989). Soft-delete; cancels
// any pending RenderUserBadgeJob and broadcasts badge_removed.
export const REMOVE_USER_BADGE = gql`
  mutation RemoveUserBadge($id: ID!) {
    removeUserBadge(id: $id) {
      id
    }
  }
`

// Content Hub (ENGG-9032) — admin-owned internal folders + uploaded assets.
// createContentHubAsset uses Apollo Upload for the file arg — the existing
// UploadLink in createGraphqlClient handles multipart transport transparently.
export const CREATE_CONTENT_HUB_FOLDER = gql`
  mutation CreateContentHubFolder(
    $name: String!
    $parentFolderId: ID
    $taxonomyTermIds: [ID!]
    $excludedTaxonomyTermIds: [ID!]
    $repAccess: String
  ) {
    createContentHubFolder(
      name: $name
      parentFolderId: $parentFolderId
      taxonomyTermIds: $taxonomyTermIds
      excludedTaxonomyTermIds: $excludedTaxonomyTermIds
      repAccess: $repAccess
    ) {
      folder {
        id
        name
      }
      errors
    }
  }
`

export const UPDATE_CONTENT_HUB_FOLDER = gql`
  mutation UpdateContentHubFolder(
    $id: ID!
    $name: String
    $parentFolderId: ID
    $clearParent: Boolean
    $taxonomyTermIds: [ID!]
    $excludedTaxonomyTermIds: [ID!]
    $repAccess: String
    $resetContents: Boolean
  ) {
    updateContentHubFolder(
      id: $id
      name: $name
      parentFolderId: $parentFolderId
      clearParent: $clearParent
      taxonomyTermIds: $taxonomyTermIds
      excludedTaxonomyTermIds: $excludedTaxonomyTermIds
      repAccess: $repAccess
      resetContents: $resetContents
    ) {
      folder {
        id
        name
      }
      errors
    }
  }
`

// ENGG-10257 — audience of a synced folder (Drive folder, Confluence space,
// SharePoint site…), inherited by every document in it and its subfolders.
export const UPDATE_CONTENT_FOLDER_AUDIENCE = gql`
  mutation UpdateContentFolderAudience(
    $id: ID!
    $taxonomyTermIds: [ID!]
    $excludedTaxonomyTermIds: [ID!]
    $repAccess: String
    $resetContents: Boolean
  ) {
    updateContentFolderAudience(
      id: $id
      taxonomyTermIds: $taxonomyTermIds
      excludedTaxonomyTermIds: $excludedTaxonomyTermIds
      repAccess: $repAccess
      resetContents: $resetContents
    ) {
      folder {
        id
        name
        repAccess
      }
      errors
    }
  }
`

export const UPDATE_CONTENT_HUB_FOLDERS_ORDER = gql`
  mutation UpdateContentHubFoldersOrder($ids: [ID!]!, $parentFolderId: ID) {
    updateContentHubFoldersOrder(ids: $ids, parentFolderId: $parentFolderId) {
      id
      name
    }
  }
`

export const DELETE_CONTENT_HUB_FOLDER = gql`
  mutation DeleteContentHubFolder($id: ID!, $mode: String!) {
    deleteContentHubFolder(id: $id, mode: $mode) {
      success
      errors
    }
  }
`

export const UPDATE_CMS_ASSISTANT_INSTRUCTIONS = gql`
  mutation UpdateCmsAssistantInstructions($instructions: String!) {
    updateCmsAssistantInstructions(instructions: $instructions) {
      instructions
      errors
    }
  }
`

// ENGG-9890 — the default note reps see in the "Email this link" step.
export const UPDATE_CMS_SHARE_EMAIL_TEMPLATE = gql`
  mutation UpdateCmsShareEmailTemplate($message: String!) {
    updateCmsShareEmailTemplate(message: $message) {
      message
      errors
    }
  }
`

// ENGG-9890 — email the admin the share email as a recipient would get it.
export const SEND_CMS_SHARE_EMAIL_SAMPLE = gql`
  mutation SendCmsShareEmailSample($message: String!) {
    sendCmsShareEmailSample(message: $message) {
      sentTo
      errors
    }
  }
`

// ENGG-9611 — save this client's Recent-questions email digest config.
export const UPSERT_RECENT_QUESTIONS_ALERT = gql`
  mutation UpsertRecentQuestionsAlert(
    $attributes: CmsRecentQuestionsAlertInput!
  ) {
    upsertRecentQuestionsAlert(attributes: $attributes) {
      id
      enabled
      intervalValue
      intervalUnit
      includeUncovered
      includeNegative
      newSinceLast
      lastNotifiedAt
      recipients {
        type
        value
        label
      }
    }
  }
`

// ENGG-9611 — email the current admin a one-off sample of the Recent-questions
// digest (last few uncovered questions) so they can preview it.
export const PREVIEW_RECENT_QUESTIONS_ALERT = gql`
  mutation PreviewRecentQuestionsAlert {
    previewRecentQuestionsAlert {
      sent
      to
    }
  }
`

export const UPSERT_CURATED_ANSWER = gql`
  mutation UpsertCuratedAnswer(
    $id: ID
    $question: String
    $answer: String
    $kind: String
    $status: String
    $verbatim: Boolean
    $sourceEventId: ID
  ) {
    upsertCuratedAnswer(
      id: $id
      question: $question
      answer: $answer
      kind: $kind
      status: $status
      verbatim: $verbatim
      sourceEventId: $sourceEventId
    ) {
      curatedAnswer {
        id
        question
        answer
        kind
        status
        verbatim
        source
        sourceEventId
        createdByName
        indexedAt
        searchable
        updatedAt
      }
      errors
    }
  }
`

export const DELETE_CURATED_ANSWER = gql`
  mutation DeleteCuratedAnswer($id: ID!) {
    deleteCuratedAnswer(id: $id) {
      success
      errors
    }
  }
`

export const ASK_CMS_SEARCH = gql`
  mutation AskCmsSearch(
    $question: String!
    $conversation: [LibraryAiChatTurnInput!]
  ) {
    askCmsSearch(question: $question, conversation: $conversation) {
      answer
      eventId
      servedVerbatim
      curatedAnswerIds
      citations {
        id
        title
        url
      }
    }
  }
`

// ENGG-10142 — the Sidekick's answer preview: the same ask as the tester above,
// answered in the Sidekick's style (the unsaved one, when given).
export const ASK_SIDEKICK_PREVIEW = gql`
  mutation AskSidekickPreview($question: String!, $answerInstructions: String) {
    askCmsSearch(
      question: $question
      surface: "sidekick"
      answerInstructions: $answerInstructions
    ) {
      answer
      servedVerbatim
      curatedAnswerIds
      citations {
        id
        title
        url
      }
      results {
        id
        title
        url
      }
    }
  }
`

export const SUGGEST_CURATED_ANSWER = gql`
  mutation SuggestCuratedAnswer($question: String!, $fromEventId: ID) {
    suggestCuratedAnswer(question: $question, fromEventId: $fromEventId) {
      question
      suggestedAnswer
      grounded
      errors
    }
  }
`

export const GENERATE_CURATED_ANSWER_CANDIDATES = gql`
  mutation GenerateCuratedAnswerCandidates(
    $sourceId: ID!
    $sourceType: String!
    $limit: Int
  ) {
    generateCuratedAnswerCandidates(
      sourceId: $sourceId
      sourceType: $sourceType
      limit: $limit
    ) {
      run {
        id
        state
        sourceTitle
      }
      errors
    }
  }
`

export const RESOLVE_CURATED_ANSWER_CANDIDATE = gql`
  mutation ResolveCuratedAnswerCandidate(
    $id: ID!
    $action: String!
    $question: String
    $answer: String
    $kind: String
    $verbatim: Boolean
    $status: String
  ) {
    resolveCuratedAnswerCandidate(
      id: $id
      action: $action
      question: $question
      answer: $answer
      kind: $kind
      verbatim: $verbatim
      status: $status
    ) {
      candidate {
        id
        status
      }
      curatedAnswer {
        id
        question
        answer
        status
        searchable
      }
      errors
    }
  }
`

export const RESOLVE_CURATED_ANSWER_CANDIDATES = gql`
  mutation ResolveCuratedAnswerCandidates(
    $runId: ID
    $ids: [ID!]
    $action: String!
    $status: String
  ) {
    resolveCuratedAnswerCandidates(
      runId: $runId
      ids: $ids
      action: $action
      status: $status
    ) {
      resolvedCount
      skippedCount
      errors
    }
  }
`

// ENGG-9890 — publish or unpublish a selection of approved answers.
export const BULK_UPDATE_CURATED_ANSWER_STATUS = gql`
  mutation BulkUpdateCuratedAnswerStatus(
    $ids: [ID!]!
    $status: String
    $verbatim: Boolean
  ) {
    bulkUpdateCuratedAnswerStatus(
      ids: $ids
      status: $status
      verbatim: $verbatim
    ) {
      updatedCount
      searchableCount
      errors
    }
  }
`

export const IMPORT_CURATED_ANSWERS = gql`
  mutation ImportCuratedAnswers(
    $rows: [CuratedAnswerImportRowInput!]!
    $fileName: String
  ) {
    importCuratedAnswers(rows: $rows, fileName: $fileName) {
      run {
        id
        state
        sourceTitle
        candidateCount
      }
      errors
    }
  }
`

export const CREATE_CONTENT_HUB_ASSET = gql`
  mutation CreateContentHubAsset(
    $name: String!
    $folderId: ID
    $file: Upload
    $externalUrl: String
    $tags: [String!]
  ) {
    createContentHubAsset(
      name: $name
      folderId: $folderId
      file: $file
      externalUrl: $externalUrl
      tags: $tags
    ) {
      asset {
        id
        title
        url
        fileType
        source
      }
      errors
    }
  }
`

// ENGG-10113 — "Make a copy" from the card and selection menus. Internal
// uploads only; the copy is "Copy of <name>" in the same folder, outside the Brain.
export const COPY_CONTENT_HUB_ASSET = gql`
  mutation CopyContentHubAsset($assetId: ID!) {
    copyContentHubAsset(assetId: $assetId) {
      asset {
        id
        title
        source
      }
      errors
    }
  }
`

export const SET_ASSET_IN_BRAIN = gql`
  mutation SetAssetInBrain(
    $assetIds: [ID!]!
    $inBrain: Boolean!
    $source: String
  ) {
    setAssetInBrain(assetIds: $assetIds, inBrain: $inBrain, source: $source) {
      assets {
        id
        inBrain
        vectorizationState
        lastVectorizedAt
      }
      errors
    }
  }
`

// ENGG-9519 — asks the Brain which enrolled CMS content should build a
// roleplay. `consideredCount` separates "nothing is enrolled" from "nothing
// fit", which need opposite copy in the panel.
export const SELECT_ROLEPLAY_BRAIN_CONTEXT = gql`
  mutation SelectRoleplayBrainContext(
    $roleplayType: String
    $description: String
    $taskTitle: String
    $taskDescription: String
    $attachedNames: [String!]
  ) {
    selectRoleplayBrainContext(
      roleplayType: $roleplayType
      description: $description
      taskTitle: $taskTitle
      taskDescription: $taskDescription
      attachedNames: $attachedNames
    ) {
      selections {
        source
        assetId
        title
        reason
      }
      consideredCount
    }
  }
`

export const UPDATE_CONTENT_HUB_ASSET = gql`
  mutation UpdateContentHubAsset(
    $id: ID!
    $name: String
    $summary: String
    $tags: [String!]
    $visibility: String
    $repAccess: String
    $availableToAi: Boolean
    $inSidekick: Boolean
    $file: Upload
    $thumbnail: Upload
    $externalUrl: String
    $taxonomyTermIds: [ID!]
    $excludedTaxonomyTermIds: [ID!]
    $customerId: ID
  ) {
    updateContentHubAsset(
      id: $id
      name: $name
      summary: $summary
      tags: $tags
      visibility: $visibility
      repAccess: $repAccess
      availableToAi: $availableToAi
      inSidekick: $inSidekick
      file: $file
      thumbnail: $thumbnail
      externalUrl: $externalUrl
      taxonomyTermIds: $taxonomyTermIds
      excludedTaxonomyTermIds: $excludedTaxonomyTermIds
      customerId: $customerId
    ) {
      asset {
        id
        title
        summary
        tags
        visibility
        availableToAi
        inSidekick
        thumbnailUrl
        fileType
        customerId
        customerName
        customerRemoved
      }
      errors
    }
  }
`

export const SYNC_CONTENT_HUB_INDEX = gql`
  mutation SyncContentHubIndex {
    syncContentHubIndex {
      enqueued
    }
  }
`

export const REGENERATE_CONTENT_HUB_ASSET_DESCRIPTION = gql`
  mutation RegenerateContentHubAssetDescription(
    $id: ID!
    $source: String
    $force: Boolean
  ) {
    regenerateContentHubAssetDescription(
      id: $id
      source: $source
      force: $force
    ) {
      enqueued
      errors
    }
  }
`

export const UPDATE_CONTENT_HUB_ASSET_DESCRIPTION = gql`
  mutation UpdateContentHubAssetDescription(
    $id: ID!
    $source: String
    $summary: String!
  ) {
    updateContentHubAssetDescription(
      id: $id
      source: $source
      summary: $summary
    ) {
      asset {
        id
        summary
      }
      errors
    }
  }
`

// External-asset counterpart to UPDATE_CONTENT_HUB_ASSET. Drive/Notion rows
// share only three admin-editable flags with internal uploads (visibility,
// available_to_ai and in_sidekick), so the write surface is intentionally narrow — the rest
// (title, tags, file, thumbnail) stays source-owned.
// ENGG-10257 — taxonomyTermIds / excludedTaxonomyTermIds: the synced item's
// audience, written the way UPDATE_CONTENT_HUB_ASSET writes an upload's. The
// modal refetches AdminContentAsset after the write, so the terms are not
// selected here.
export const UPDATE_CONTENT_HUB_EXTERNAL_ASSET = gql`
  mutation UpdateContentHubExternalAsset(
    $id: ID!
    $source: String!
    $visibility: String
    $availableToAi: Boolean
    $inSidekick: Boolean
    $customerId: ID
    $taxonomyTermIds: [ID!]
    $excludedTaxonomyTermIds: [ID!]
    $repAccess: String
  ) {
    updateContentHubExternalAsset(
      id: $id
      source: $source
      visibility: $visibility
      availableToAi: $availableToAi
      inSidekick: $inSidekick
      customerId: $customerId
      taxonomyTermIds: $taxonomyTermIds
      excludedTaxonomyTermIds: $excludedTaxonomyTermIds
      repAccess: $repAccess
    ) {
      asset {
        id
        visibility
        repAccess
        availableToAi
        inSidekick
        customerId
        customerName
        customerRemoved
      }
      errors
    }
  }
`

export const DELETE_CONTENT_HUB_ASSET = gql`
  mutation DeleteContentHubAsset($id: ID!) {
    deleteContentHubAsset(id: $id) {
      deletedId
      errors
    }
  }
`

// ENGG-9571 — the File Details flags applied to a whole selection.
//
// One `source` per call, not (id, source) pairs: ids are only unique WITHIN a
// source, so the backend scopes every write by client AND source. A mixed
// selection is therefore two calls from here, one per group.
//
// repAccess / availableToAll / folderId exist on uploads only — the backend
// refuses them outright for Drive/Notion/Confluence rather than silently
// dropping half the request, so the bar must not offer them for those.
export const BULK_UPDATE_CONTENT_HUB_ASSETS = gql`
  mutation BulkUpdateContentHubAssets(
    $assetIds: [ID!]!
    # Non-null: the backend argument is required, and GraphQL rejects a
    # nullable variable in a non-null position at validation time — the whole
    # document would fail, not just a call that omitted it.
    $source: String!
    $visibility: String
    $availableToAi: Boolean
    $inSidekick: Boolean
    $repAccess: String
    $availableToAll: Boolean
    $folderId: ID
  ) {
    bulkUpdateContentHubAssets(
      assetIds: $assetIds
      source: $source
      visibility: $visibility
      availableToAi: $availableToAi
      inSidekick: $inSidekick
      repAccess: $repAccess
      availableToAll: $availableToAll
      folderId: $folderId
    ) {
      assets {
        id
        visibility
        availableToAi
        inSidekick
        repAccess
      }
      errors
    }
  }
`

// Fire-and-forget analytics write for a per-asset action (click / open in
// tab / copy link / download). Powers the Content Hub Analytics dashboard
// KPIs, chart, leaderboard, and per-asset engagement log — all of which
// read from cms_asset_engagements. Backend only accepts VectorizedContentItem
// ids today (Drive + Notion); internal ContentHubAsset uploads are not
// tracked yet (would require extending the resolver).
export const RECORD_CMS_ASSET_ENGAGEMENT = gql`
  mutation RecordCmsAssetEngagement(
    $assetId: ID!
    $actionType: String!
    $source: String
    $eventId: ID
    $sourceContext: String
    $sourceUrl: String
  ) {
    recordCmsAssetEngagement(
      assetId: $assetId
      actionType: $actionType
      source: $source
      eventId: $eventId
      sourceContext: $sourceContext
      sourceUrl: $sourceUrl
    ) {
      id
    }
  }
`

// ENGG-9528 — mint a tokenized share link whose landing page identifies the
// viewer before rendering the document in-page. Idempotent per
// (asset, sharer, recipientEmail): resharing with the same recipient returns
// the existing live link.
export const CREATE_CMS_ASSET_SHARE_LINK = gql`
  mutation CreateCmsAssetShareLink(
    $assetId: ID!
    $source: String
    $recipientName: String
    $recipientEmail: String
    $recipientCompany: String
    $note: String
    $ttlDays: Int
    $allowDownload: Boolean
    $accessLevel: String
    $sourceContext: String
  ) {
    createCmsAssetShareLink(
      assetId: $assetId
      source: $source
      recipientName: $recipientName
      recipientEmail: $recipientEmail
      recipientCompany: $recipientCompany
      note: $note
      ttlDays: $ttlDays
      allowDownload: $allowDownload
      accessLevel: $accessLevel
      sourceContext: $sourceContext
    ) {
      id
      slug
      shareUrl
      recipientName
      recipientEmail
      recipientCompany
      note
      allowDownload
      accessLevel
      clickCount
      expiresAt
      createdAt
    }
  }
`

// Server-side wrapper around a Content Hub asset download. Records the
// `downloaded` engagement (Drive/Notion only — internal uploads aren't
// tracked in cms_asset_engagements yet) and returns the URL the client
// should open. Keeps tracking authoritative on the server rather than
// trusting the client to fire the engagement mutation in parallel.
export const DOWNLOAD_CMS_ASSET = gql`
  mutation DownloadCmsAsset(
    $assetId: ID!
    $source: String!
    $sourceContext: String
    $sourceUrl: String
  ) {
    downloadCmsAsset(
      assetId: $assetId
      source: $source
      sourceContext: $sourceContext
      sourceUrl: $sourceUrl
    ) {
      url
      filename
    }
  }
`

// Self-serve Events (POC): creates an event as a section of fixed-date
// GROUP_EVENT tasks inside the partner's workflow. Returns the event section id.
export const CREATE_PARTNER_EVENT = gql`
  mutation CreatePartnerEvent($attributes: PartnerEventInput!) {
    createPartnerEvent(attributes: $attributes) {
      id
    }
  }
`

// Updates an existing event: renames it and rebuilds its session tasks.
export const UPDATE_PARTNER_EVENT = gql`
  mutation UpdatePartnerEvent($id: ID!, $attributes: PartnerEventInput!) {
    updatePartnerEvent(id: $id, attributes: $attributes) {
      id
    }
  }
`

// Replaces the whole set of segments making up the audience of an
// ALREADY-APPROVED event (ENGG-9570) — their members are unioned. Events can be
// approved with no audience — the calendar event and Zoom link are created for
// the facilitators, and the people come later. The enrol/uninvite work runs
// async, and the event page refetches on the websocket push.
//
// An empty (or null) dateCohortIds clears the audience and removes everyone.
// Dropping ONE segment from several removes only the people it uniquely
// contributed; anyone also in a segment that stays keeps their place.
export const ASSIGN_PARTNER_EVENT_AUDIENCE = gql`
  mutation AssignPartnerEventAudience($id: ID!, $dateCohortIds: [String!]) {
    assignPartnerEventAudience(id: $id, dateCohortIds: $dateCohortIds) {
      id
      audienceName
      dateCohortIds
      # The distinct union, not the sum of the segments' own counts.
      memberCount
      audienceSegments {
        id
        name
        description
        memberCount
      }
    }
  }
`

// ENGG-9661 — My Events' bulk "Add tags" sends only additions; a single
// event's "Edit tags" sends the difference from what it showed. Returns each
// event's tags so the rows update from the Apollo cache.
export const UPDATE_PARTNER_EVENT_TAGS = gql`
  mutation UpdatePartnerEventTags(
    $ids: [ID!]!
    $addTagIds: [ID!]
    $removeTagIds: [ID!]
  ) {
    updatePartnerEventTags(
      ids: $ids
      addTagIds: $addTagIds
      removeTagIds: $removeTagIds
    ) {
      id
      templateTags {
        id
        name
        color
      }
    }
  }
`

// ENGG-10118: moves one deployed session to a new date and time. The backend
// pushes the change to the calendar invite and the Zoom meeting/webinar and
// records it in the event's schedule history; the page refetches for both.
export const RESCHEDULE_PARTNER_EVENT_SESSION = gql`
  mutation ReschedulePartnerEventSession(
    $id: ID!
    $sessionId: ID!
    $date: String!
    $startTime: String!
    $endTime: String!
  ) {
    reschedulePartnerEventSession(
      id: $id
      sessionId: $sessionId
      date: $date
      startTime: $startTime
      endTime: $endTime
    ) {
      id
    }
  }
`

// Deletes an event. A deployed event also tears down its calendar events,
// Zoom meetings, tasks and template tasks (async).
export const DELETE_PARTNER_EVENT = gql`
  mutation DeletePartnerEvent($id: ID!) {
    deletePartnerEvent(id: $id) {
      success
    }
  }
`

// ENGG-9123: store and validate an API key for an external meeting source. One
// mutation serves every key-based provider — the provider owns where its
// credential lives, so adding a source needs no new mutation here.
export const CONNECT_INTEGRATION = gql`
  mutation ConnectIntegration($source: String!, $apiKey: String!) {
    connectIntegration(source: $source, apiKey: $apiKey) {
      key
      label
      connected
      verifiedAt
      lastError
    }
  }
`

// ENGG-10154: whether rooms may use screens from the source's call recordings.
// Turning it on is refused when the source is not connected or the server does
// not read recordings; turning it off always works.
export const UPDATE_MEETING_SOURCE_SETTINGS = gql`
  mutation UpdateMeetingSourceSettings(
    $source: String!
    $useRecordings: Boolean!
  ) {
    updateMeetingSourceSettings(
      source: $source
      useRecordings: $useRecordings
    ) {
      key
      connected
      supportsRecordings
      useRecordings
    }
  }
`

// ENGG-10423 — which recorded calls the client's reps may use in sales rooms:
// "own" (calls they organized or attended; admins see all) or "all".
export const UPDATE_DSR_CALL_VISIBILITY = gql`
  mutation UpdateDsrCallVisibility($visibility: String!) {
    updateDsrCallVisibility(visibility: $visibility)
  }
`

// Removes the stored credential. Cached meetings are deliberately kept, so an
// accidental disconnect doesn't force a full re-fetch against a rate-limited API.
export const DISCONNECT_INTEGRATION = gql`
  mutation DisconnectIntegration($source: String!) {
    disconnectIntegration(source: $source) {
      key
      label
      connected
      verifiedAt
      lastError
    }
  }
`

// =====================================================================
// Roleplay Text Simulation (ENGG-8927)
// =====================================================================

export const AI_ROLEPLAY_SIMULATION_FIELDS = gql`
  fragment AiRoleplaySimulationFields on AiRoleplaySimulation {
    id
    templateTaskId
    status
    traineePrompt
    traineePromptSource
    firstMessage
    successCriteria
    criteriaPassed
    criteriaAssessment
    batchKey
    runIndex
    runCount
    profile
    label
    situation
    testCriteria
    coversCriterionIds
    passChecks
    cellResults
    testKind
    setupTurns
    testExamples
    validation
    maxAgentTurns
    transcript
    endReason
    agentTurnCount
    traineeTurnCount
    promptTokens
    completionTokens
    totalTokens
    modelVersions
    currentTurn
    stageUpdatedAt
    errorMessage
    createdAt
    updatedAt
    createdByUser {
      id
      firstName
      lastName
    }
  }
`

// One test = 1 or 5 runs of the same setup (traineePrompt + runCount), OR a
// criteria matrix (criteria + conversations from planAiRoleplayTestSuite: one
// row per conversation). Either way the rows come back for the dialog to
// render and then poll by batchKey.
export const CREATE_AI_ROLEPLAY_SIMULATION_BATCH = gql`
  ${AI_ROLEPLAY_SIMULATION_FIELDS}
  mutation CreateAiRoleplaySimulationBatch(
    $templateTaskId: ID!
    $traineePrompt: String
    $traineePromptSource: String
    $firstMessage: String
    $successCriteria: String
    $maxAgentTurns: Int
    $runCount: Int
    $criteria: [AiRoleplayTestCriterionInput!]
    $conversations: [AiRoleplayTestConversationInput!]
  ) {
    createAiRoleplaySimulationBatch(
      templateTaskId: $templateTaskId
      traineePrompt: $traineePrompt
      traineePromptSource: $traineePromptSource
      firstMessage: $firstMessage
      successCriteria: $successCriteria
      maxAgentTurns: $maxAgentTurns
      runCount: $runCount
      criteria: $criteria
      conversations: $conversations
    ) {
      ...AiRoleplaySimulationFields
    }
  }
`

// Step one of a criteria-matrix test: free-text criteria in, a reviewable plan
// out (classified criteria, the fewest conversations that cover them, and a
// trainee prompt per conversation). Nothing is stored until the admin runs it.
//
// Planning runs in a background job: this returns the pending request row and
// the Test Lab polls GET_AI_ROLEPLAY_TEST_SUITE_PLAN_REQUEST until it is
// completed or failed. (The synchronous predecessor could not finish a large
// criteria list inside the load balancer's 60-second request limit.)
export const START_AI_ROLEPLAY_TEST_SUITE_PLAN = gql`
  mutation StartAiRoleplayTestSuitePlan(
    $templateTaskId: ID!
    $criteria: [String!]!
  ) {
    startAiRoleplayTestSuitePlan(
      templateTaskId: $templateTaskId
      criteria: $criteria
    ) {
      id
      status
      errorMessage
    }
  }
`

// "Save this turn as a test": the conversation before one of the persona's
// turns, frozen with what the persona should do there. It runs as a
// single-reply test in every later test set on the task.
export const SAVE_AI_ROLEPLAY_TURN_AS_TEST = gql`
  mutation SaveAiRoleplayTurnAsTest(
    $simulationId: ID!
    $turnIndex: Int!
    $expectation: String!
    $name: String
  ) {
    saveAiRoleplayTurnAsTest(
      simulationId: $simulationId
      turnIndex: $turnIndex
      expectation: $expectation
      name: $name
    ) {
      id
      name
      expectation
      criterionId
    }
  }
`

export const DELETE_AI_ROLEPLAY_SAVED_TEST = gql`
  mutation DeleteAiRoleplaySavedTest($id: ID!) {
    deleteAiRoleplaySavedTest(id: $id)
  }
`

// Free-form Q&A over picked real transcripts plus this batch's simulated ones.
// Stateless on the server: the whole thread is resent each turn.
export const ASK_AI_ROLEPLAY_TRANSCRIPT_CHAT = gql`
  mutation AskAiRoleplayTranscriptChat(
    $templateTaskId: ID!
    $chatSessionIds: [ID!]
    $simulationIds: [ID!]
    $messages: [ChatMessageInput!]!
  ) {
    askAiRoleplayTranscriptChat(
      templateTaskId: $templateTaskId
      chatSessionIds: $chatSessionIds
      simulationIds: $simulationIds
      messages: $messages
    ) {
      reply
      promptTokens
      completionTokens
      realTranscriptCount
      simulatedTranscriptCount
    }
  }
`

export const GENERATE_AI_ROLEPLAY_TRAINEE_PROMPT = gql`
  mutation GenerateAiRoleplayTraineePrompt(
    $templateTaskId: ID!
    $chatSessionIds: [ID!]
  ) {
    generateAiRoleplayTraineePrompt(
      templateTaskId: $templateTaskId
      chatSessionIds: $chatSessionIds
    ) {
      traineePrompt
    }
  }
`

export const CREATE_CLIENT_DATAWAREHOUSE_REPORT_SYNC = gql`
  mutation CreateClientDatawarehouseReportSync(
    $userId: ID!
    $frequency: String!
    $s3Bucket: String!
    $s3Region: String!
    $s3Prefix: String
    $s3AccessKeyId: String!
    $s3SecretAccessKey: String!
    $s3AuthMode: String
    $s3RoleArn: String
    $s3ExternalId: String
    $reports: [ClientDatawarehouseReportSyncReportInput!]!
  ) {
    createClientDatawarehouseReportSync(
      userId: $userId
      frequency: $frequency
      s3Bucket: $s3Bucket
      s3Region: $s3Region
      s3Prefix: $s3Prefix
      s3AccessKeyId: $s3AccessKeyId
      s3SecretAccessKey: $s3SecretAccessKey
      s3AuthMode: $s3AuthMode
      s3RoleArn: $s3RoleArn
      s3ExternalId: $s3ExternalId
      reports: $reports
    ) {
      id
    }
  }
`

export const UPDATE_CLIENT_DATAWAREHOUSE_REPORT_SYNC = gql`
  mutation UpdateClientDatawarehouseReportSync(
    $id: ID!
    $userId: ID
    $frequency: String
    $enabled: Boolean
    $s3Bucket: String
    $s3Region: String
    $s3Prefix: String
    $s3AccessKeyId: String
    $s3SecretAccessKey: String
    $s3AuthMode: String
    $s3RoleArn: String
    $s3ExternalId: String
    $reports: [ClientDatawarehouseReportSyncReportInput!]
  ) {
    updateClientDatawarehouseReportSync(
      id: $id
      userId: $userId
      frequency: $frequency
      enabled: $enabled
      s3Bucket: $s3Bucket
      s3Region: $s3Region
      s3Prefix: $s3Prefix
      s3AccessKeyId: $s3AccessKeyId
      s3SecretAccessKey: $s3SecretAccessKey
      s3AuthMode: $s3AuthMode
      s3RoleArn: $s3RoleArn
      s3ExternalId: $s3ExternalId
      reports: $reports
    ) {
      id
    }
  }
`

export const DELETE_CLIENT_DATAWAREHOUSE_REPORT_SYNC = gql`
  mutation DeleteClientDatawarehouseReportSync($id: ID!) {
    deleteClientDatawarehouseReportSync(id: $id) {
      success
    }
  }
`

export const TEST_CLIENT_DATAWAREHOUSE_S3_CONNECTION = gql`
  mutation TestClientDatawarehouseS3Connection(
    $id: ID
    $s3Bucket: String
    $s3Region: String
    $s3Prefix: String
    $s3AuthMode: String
    $s3AccessKeyId: String
    $s3SecretAccessKey: String
    $s3RoleArn: String
    $s3ExternalId: String
  ) {
    testClientDatawarehouseS3Connection(
      id: $id
      s3Bucket: $s3Bucket
      s3Region: $s3Region
      s3Prefix: $s3Prefix
      s3AuthMode: $s3AuthMode
      s3AccessKeyId: $s3AccessKeyId
      s3SecretAccessKey: $s3SecretAccessKey
      s3RoleArn: $s3RoleArn
      s3ExternalId: $s3ExternalId
    ) {
      ok
      testedAt
      steps {
        key
        label
        status
        message
      }
    }
  }
`

export const TRIGGER_CLIENT_DATAWAREHOUSE_REPORT_SYNC = gql`
  mutation TriggerClientDatawarehouseReportSync($id: ID!) {
    triggerClientDatawarehouseReportSync(id: $id) {
      id
      status
      createdAt
    }
  }
`

// =====================================================================
// CRM connection and stage mappings (ENGG-9746)
// =====================================================================

// Verified against the provider BEFORE the token is kept; a refused token leaves
// nothing behind.
export const CONNECT_CRM = gql`
  mutation ConnectCrm(
    $provider: String!
    $accessToken: String
    $credentials: CrmCredentialsInput
  ) {
    connectCrm(
      provider: $provider
      accessToken: $accessToken
      credentials: $credentials
    ) {
      provider
      label
      connected
      externalAccountId
      accountUrl
      status
      lastError
      verifiedAt
    }
  }
`

// Removes the credential; mappings and recorded transitions stay.
export const DISCONNECT_CRM = gql`
  mutation DisconnectCrm($provider: String!) {
    disconnectCrm(provider: $provider) {
      provider
      label
      connected
      status
    }
  }
`

export const UPSERT_CRM_STAGE_MAPPING = gql`
  mutation UpsertCrmStageMapping(
    $provider: String!
    $externalPipelineId: String!
    $externalStageId: String!
    $salesStage: SalesStage!
    $label: String
  ) {
    upsertCrmStageMapping(
      provider: $provider
      externalPipelineId: $externalPipelineId
      externalStageId: $externalStageId
      salesStage: $salesStage
      label: $label
    ) {
      id
      externalPipelineId
      externalStageId
      salesStage
      label
    }
  }
`

export const DELETE_CRM_STAGE_MAPPING = gql`
  mutation DeleteCrmStageMapping($id: ID!) {
    deleteCrmStageMapping(id: $id)
  }
`

// ENGG-9829 / ENGG-10240 — the admin's writer of the sales-automation switch
// (the backend's `sales_automation:enable` rake task is the other). Every
// argument is optional: omitting one leaves it alone, so the card sends only the
// fields that changed. `defaultRoomTemplateId: null` clears the template and
// `fallbackOwnerUserId: null` clears the fallback owner; `creationMode` cannot
// be null. ENGG-10296: `organizerEmails` replaces the whole list (`[]` = any
// organizer; null is refused). ENGG-10421: `callKinds` replaces the whole set
// (at least one; `[]` and null are refused).
export const UPSERT_SALES_AUTOMATION_SETTING = gql`
  mutation UpsertSalesAutomationSetting(
    $callIngestionEnabled: Boolean
    $creationMode: SalesAutomationCreationMode
    $defaultRoomTemplateId: ID
    $fallbackOwnerUserId: ID
    $lookbackMinutes: Int
    $organizerEmails: [String!]
    $callKinds: [SalesAutomationCallKind!]
  ) {
    upsertSalesAutomationSetting(
      callIngestionEnabled: $callIngestionEnabled
      creationMode: $creationMode
      defaultRoomTemplateId: $defaultRoomTemplateId
      fallbackOwnerUserId: $fallbackOwnerUserId
      lookbackMinutes: $lookbackMinutes
      organizerEmails: $organizerEmails
      callKinds: $callKinds
    ) {
      ...SalesAutomationSettingFields
    }
  }
  ${FRAGMENT_SALES_AUTOMATION_SETTING_FIELDS}
`

// ENGG-10296 — "Build a room anyway" for a call the sales-call screen skipped
// (the backend's retryCallEvent, which also retries a failed delivery). Admins
// only for a skipped call; the answer is the delivery after its dispatch, with
// the list's fields, so a call that stays listed is updated in the cache.
export const RETRY_CALL_EVENT = gql`
  mutation RetryCallEvent($eventId: ID!) {
    retryCallEvent(eventId: $eventId) {
      id
      status
      detail
      subject
      organizerEmail
      occurredAt
      resolvedDomain
      screenReason
      screenConfidence
      screenOverriddenAt
    }
  }
`

// ENGG-9869 — save the admin's client's Sidekick setup (the whole setup at once).
export const UPSERT_SIDEKICK_SETTINGS = gql`
  mutation UpsertSidekickSettings($attributes: SidekickSettingsInput!) {
    upsertSidekickSettings(attributes: $attributes) {
      enabled
      botName
      defaultGoals
      competitorNames
      callContext
      answerInstructions
      defaultAnswerInstructions
      followUpInstructions
      defaultFollowUpInstructions
      updatedAt
      roles {
        name
        blurb
        callTypes {
          name
          goals
        }
      }
      keyTerms {
        term
        heardAs
      }
    }
  }
`

// ENGG-9844 — Anonymous Surveys.
const ANONYMOUS_SURVEY_RESULT = `
  id
  title
  introText
  thankYouMessage
  status
  effectiveStatus
  closesAt
  shareToken
  responseCount
  startedCount
  lastResponseOn
  structureEditable
  ownerId
  ownerName
  ownedByViewer
  createdAt
  questions {
    id
    position
    questionType
    prompt
    promptText
    required
    options {
      id
      label
    }
    scaleMin
    scaleMax
    scaleMinLabel
    scaleMaxLabel
  }
  shareLinks {
    id
    token
    active
    createdAt
    revokedAt
  }
`

export const CREATE_ANONYMOUS_SURVEY = gql`
  mutation CreateAnonymousSurvey($attributes: AnonymousSurveyInput!) {
    createAnonymousSurvey(attributes: $attributes) {
      ${ANONYMOUS_SURVEY_RESULT}
    }
  }
`

export const UPDATE_ANONYMOUS_SURVEY = gql`
  mutation UpdateAnonymousSurvey($id: ID!, $attributes: AnonymousSurveyInput!) {
    updateAnonymousSurvey(id: $id, attributes: $attributes) {
      ${ANONYMOUS_SURVEY_RESULT}
    }
  }
`

// Publishing is its own mutation rather than a field on the update: opening a
// link to the world is the one action here that can't be taken back, and it
// must not be possible to trigger it as a side effect of saving a typo fix.
export const SET_ANONYMOUS_SURVEY_STATUS = gql`
  mutation SetAnonymousSurveyStatus($id: ID!, $status: String!) {
    setAnonymousSurveyStatus(id: $id, status: $status) {
      ${ANONYMOUS_SURVEY_RESULT}
    }
  }
`

export const REGENERATE_ANONYMOUS_SURVEY_LINK = gql`
  mutation RegenerateAnonymousSurveyLink($id: ID!) {
    regenerateAnonymousSurveyLink(id: $id) {
      ${ANONYMOUS_SURVEY_RESULT}
    }
  }
`

export const DELETE_ANONYMOUS_SURVEY = gql`
  mutation DeleteAnonymousSurvey($id: ID!) {
    deleteAnonymousSurvey(id: $id)
  }
`

// ENGG-9520 — point one HubSpot deal stage at a scorecard, or clear it (null).
export const SET_PRACTICE_STAGE_SCORECARD = gql`
  mutation SetPracticeStageScorecard(
    $pipelineId: String!
    $stageId: String!
    $scorecardTemplateId: ID
  ) {
    setPracticeStageScorecard(
      pipelineId: $pipelineId
      stageId: $stageId
      scorecardTemplateId: $scorecardTemplateId
    ) {
      pipelineId
      stageId
      stageLabel
      scorecardTemplate {
        id
        name
      }
    }
  }
`

// ENGG-9520 — custom practice stages: create, update (name and/or scorecard;
// scorecardTemplateId sent as null clears it, left out leaves it), move one
// place up/down, and delete.
const PRACTICE_CUSTOM_STAGE_FIELDS = `
  id
  name
  position
  scorecardTemplate {
    id
    name
  }
`

export const CREATE_PRACTICE_CUSTOM_STAGE = gql`
  mutation CreatePracticeCustomStage($name: String!, $scorecardTemplateId: ID) {
    createPracticeCustomStage(name: $name, scorecardTemplateId: $scorecardTemplateId) {
      ${PRACTICE_CUSTOM_STAGE_FIELDS}
    }
  }
`

export const RENAME_PRACTICE_CUSTOM_STAGE = gql`
  mutation RenamePracticeCustomStage($id: ID!, $name: String!) {
    updatePracticeCustomStage(id: $id, name: $name) {
      ${PRACTICE_CUSTOM_STAGE_FIELDS}
    }
  }
`

export const SET_PRACTICE_CUSTOM_STAGE_SCORECARD = gql`
  mutation SetPracticeCustomStageScorecard($id: ID!, $scorecardTemplateId: ID) {
    updatePracticeCustomStage(id: $id, scorecardTemplateId: $scorecardTemplateId) {
      ${PRACTICE_CUSTOM_STAGE_FIELDS}
    }
  }
`

export const MOVE_PRACTICE_CUSTOM_STAGE = gql`
  mutation MovePracticeCustomStage($id: ID!, $direction: String!) {
    movePracticeCustomStage(id: $id, direction: $direction) {
      ${PRACTICE_CUSTOM_STAGE_FIELDS}
    }
  }
`

export const DELETE_PRACTICE_CUSTOM_STAGE = gql`
  mutation DeletePracticeCustomStage($id: ID!) {
    deletePracticeCustomStage(id: $id)
  }
`

// "Why?" on the Roleplay matrix's Average row. Returns the stored diagnosis for
// unchanged inputs, an identical run already in flight, or a new pending row
// that ROLEPLAY_QUESTION_DIAGNOSIS polls. Omit templateTaskQuestionId to
// diagnose the roleplay's total. Admins only.
export const START_ROLEPLAY_ADVISOR_RUN = gql`
  mutation StartRoleplayAdvisorRun(
    $templateTaskId: ID!
    $kind: String!
    $feedback: String
    $templateTaskQuestionId: ID
  ) {
    startRoleplayAdvisorRun(
      templateTaskId: $templateTaskId
      kind: $kind
      feedback: $feedback
      templateTaskQuestionId: $templateTaskQuestionId
    ) {
      id
      kind
      feedback
      status
      stage
      result
      errorMessage
      createdByName
      createdAt
      calls
    }
  }
`

export const START_ROLEPLAY_QUESTION_DIAGNOSIS = gql`
  mutation StartRoleplayQuestionDiagnosis(
    $templateTaskId: ID!
    $templateTaskQuestionId: ID
    $attemptPick: String
    $dateFrom: ISO8601DateTime
  ) {
    startRoleplayQuestionDiagnosis(
      templateTaskId: $templateTaskId
      templateTaskQuestionId: $templateTaskQuestionId
      attemptPick: $attemptPick
      dateFrom: $dateFrom
    ) {
      id
      status
      attemptCount
      diagnosis
      errorMessage
      reps
    }
  }
`

// ENGG-9979 — Call Scorecards
export const CREATE_CALL_BEHAVIOR = gql`
  mutation CreateCallBehavior($input: CallBehaviorInput!) {
    createCallBehavior(input: $input) {
      ...CallBehaviorFields
    }
  }
  ${FRAGMENT_CALL_BEHAVIOR_FIELDS}
`

// ENGG-10393 — after saving an item: going forward only, or past calls too.
export const APPLY_CALL_BEHAVIOR_CHANGE = gql`
  mutation ApplyCallBehaviorChange(
    $callBehaviorId: ID!
    $rescore: String!
    $days: Int
    $last: Int
  ) {
    applyCallBehaviorChange(
      callBehaviorId: $callBehaviorId
      rescore: $rescore
      days: $days
      last: $last
    )
  }
`

export const UPDATE_CALL_BEHAVIOR = gql`
  mutation UpdateCallBehavior(
    $id: ID!
    $input: CallBehaviorInput!
    $rescore: Boolean
  ) {
    updateCallBehavior(id: $id, input: $input, rescore: $rescore) {
      ...CallBehaviorFields
    }
  }
  ${FRAGMENT_CALL_BEHAVIOR_FIELDS}
`

// ENGG-10221 — Scorecard setup › Methodology.
export const UPDATE_CALL_SCORECARD_METHODOLOGY = gql`
  mutation UpdateCallScorecardMethodology(
    $name: String
    $letters: JSON
    $roles: JSON
  ) {
    updateCallScorecardMethodology(
      name: $name
      letters: $letters
      roles: $roles
    ) {
      ...CallScorecardMethodologyFields
    }
  }
  ${FRAGMENT_CALL_SCORECARD_METHODOLOGY_FIELDS}
`

export const SET_CALL_BEHAVIOR_ENABLED = gql`
  mutation SetCallBehaviorEnabled($id: ID!, $enabled: Boolean!) {
    setCallBehaviorEnabled(id: $id, enabled: $enabled) {
      id
      enabled
    }
  }
`

export const DRAFT_CALL_BEHAVIOR = gql`
  mutation DraftCallBehavior($description: String!) {
    draftCallBehavior(description: $description) {
      name
      shortLabel
      stage
      definition
      applies
      trigger
      levels
    }
  }
`

export const UPDATE_RECORDED_CALL_TYPE = gql`
  mutation UpdateRecordedCallType($id: ID!, $callType: Int!) {
    updateRecordedCallType(id: $id, callType: $callType) {
      id
      callType
      callTypeSource
    }
  }
`

export const FLAG_CALL_BEHAVIOR_SCORE = gql`
  mutation FlagCallBehaviorScore(
    $callBehaviorScoreId: ID!
    $suggestedScore: String!
    $note: String
  ) {
    flagCallBehaviorScore(
      callBehaviorScoreId: $callBehaviorScoreId
      suggestedScore: $suggestedScore
      note: $note
    ) {
      id
      flaggedSuggestion
    }
  }
`

export const CREATE_CALL_SAVED_REPORT = gql`
  mutation CreateCallSavedReport(
    $name: String!
    $description: String
    $filters: JSON!
    $view: String
  ) {
    createCallSavedReport(
      name: $name
      description: $description
      filters: $filters
      view: $view
    ) {
      id
      name
      description
      filters
      view
    }
  }
`

export const RETRY_RECORDED_CALL = gql`
  mutation RetryRecordedCall($id: ID!) {
    retryRecordedCall(id: $id) {
      id
      status
      lastError
    }
  }
`

export const RETRY_FAILED_CALLS = gql`
  mutation RetryFailedCalls {
    retryFailedCalls
  }
`

// ENGG-10090 — the Gong/Avoma switch on Settings → Call Recorder
export const SET_CALL_RECORDER = gql`
  mutation SetCallRecorder($provider: String!) {
    setCallRecorder(provider: $provider)
  }
`

// ENGG-10018 — skill practice built from scored calls
export const BUILD_CALL_PRACTICE_ROLEPLAY = gql`
  mutation BuildCallPracticeRoleplay(
    $userId: ID!
    $code: String!
    $recordedCallIds: [ID!]!
    $difficulty: String
    $format: String
  ) {
    buildCallPracticeRoleplay(
      userId: $userId
      code: $code
      recordedCallIds: $recordedCallIds
      difficulty: $difficulty
      format: $format
    ) {
      id
      status
    }
  }
`

// ENGG-10065 — Call Scorecards v1.2: Stages and deal plays
const CALL_TYPE_MUTATION_FIELDS = `
  id
  slot
  position
  name
  shortLabel
  description
  kind
  crmStages
  titleKeywords
  phrases
  useCrmStage
  useAi
  carryGaps
  maxCarry
  behaviorOrder
`

export const CREATE_CALL_SCORECARD_CALL_TYPE = gql`
  mutation CreateCallScorecardCallType(
    $input: CallScorecardCallTypeInput!
    $afterId: ID
  ) {
    createCallScorecardCallType(input: $input, afterId: $afterId) {
      ${CALL_TYPE_MUTATION_FIELDS}
    }
  }
`

export const UPDATE_CALL_PRACTICE_ROLEPLAY = gql`
  mutation UpdateCallPracticeRoleplay(
    $id: ID!
    $title: String
    $difficulty: String
    $format: String
  ) {
    updateCallPracticeRoleplay(
      id: $id
      title: $title
      difficulty: $difficulty
      format: $format
    ) {
      id
      title
      difficulty
      format
      timeLimitMinutes
      personaDisposition
    }
  }
`

export const UPDATE_CALL_SCORECARD_CALL_TYPE = gql`
  mutation UpdateCallScorecardCallType(
    $id: ID!
    $input: CallScorecardCallTypeInput!
  ) {
    updateCallScorecardCallType(id: $id, input: $input) {
      ${CALL_TYPE_MUTATION_FIELDS}
    }
  }
`

export const ASSIGN_CALL_PRACTICE_ROLEPLAY = gql`
  mutation AssignCallPracticeRoleplay(
    $id: ID!
    $userIds: [ID!]!
    $dueOn: ISO8601Date
    $passingAttempts: Int
    $passScore: Int
    $trackImpact: Boolean
  ) {
    assignCallPracticeRoleplay(
      id: $id
      userIds: $userIds
      dueOn: $dueOn
      passingAttempts: $passingAttempts
      passScore: $passScore
      trackImpact: $trackImpact
    ) {
      id
      assignments {
        id
        userId
        userName
        status
        dueOn
      }
    }
  }
`

export const DELETE_CALL_SCORECARD_CALL_TYPE = gql`
  mutation DeleteCallScorecardCallType($id: ID!) {
    deleteCallScorecardCallType(id: $id)
  }
`

export const REORDER_CALL_SCORECARD_CALL_TYPES = gql`
  mutation ReorderCallScorecardCallTypes($ids: [ID!]!) {
    reorderCallScorecardCallTypes(ids: $ids) {
      id
      position
    }
  }
`

export const SET_CALL_BEHAVIOR_APPLICABILITY = gql`
  mutation SetCallBehaviorApplicability(
    $behaviorId: ID!
    $callTypeId: ID!
    $value: String!
  ) {
    setCallBehaviorApplicability(
      behaviorId: $behaviorId
      callTypeId: $callTypeId
      value: $value
    ) {
      id
      applies
      edited
      appliesEdited
    }
  }
`

export const ADD_CALL_SCORECARD_DEAL_PLAY = gql`
  mutation AddCallScorecardDealPlay($dealKey: ID!, $text: String!) {
    addCallScorecardDealPlay(dealKey: $dealKey, text: $text) {
      id
      text
      authorName
      createdAt
    }
  }
`

export const UPDATE_SLACK_CAPABILITIES = gql`
  mutation UpdateSlackCapabilities(
    $dmEmployees: Boolean
    $postToChannels: Boolean
    $customSender: Boolean
    $addToChannels: Boolean
    $joinPublicChannels: Boolean
    $createChannels: Boolean
    $channelCanvas: Boolean
    $removeFromChannels: Boolean
  ) {
    updateSlackCapabilities(
      dmEmployees: $dmEmployees
      postToChannels: $postToChannels
      customSender: $customSender
      addToChannels: $addToChannels
      joinPublicChannels: $joinPublicChannels
      createChannels: $createChannels
      channelCanvas: $channelCanvas
      removeFromChannels: $removeFromChannels
    ) {
      dmEmployees
      postToChannels
      customSender
      addToChannels
      joinPublicChannels
      createChannels
      channelCanvas
      removeFromChannels
    }
  }
`

export const START_ROLEPLAY_TRANSCRIPT_MOCK = gql`
  mutation StartRoleplayTranscriptMock(
    $templateTaskId: ID!
    $transcript: String!
    $employeeName: String
    $employeeVoice: String
  ) {
    startRoleplayTranscriptMock(
      templateTaskId: $templateTaskId
      transcript: $transcript
      employeeName: $employeeName
      employeeVoice: $employeeVoice
    ) {
      id
      status
      stage
    }
  }
`

export const RERENDER_ROLEPLAY_TRANSCRIPT_MOCK_AUDIO = gql`
  mutation RerenderRoleplayTranscriptMockAudio(
    $id: ID!
    $employeeVoice: String
  ) {
    rerenderRoleplayTranscriptMockAudio(
      id: $id
      employeeVoice: $employeeVoice
    ) {
      id
      audioStatus
      employeeVoice
    }
  }
`

// "Rescore" on a finished mock: both calls graded again on the current
// scorecard, in the background (poll `rescoring`).
export const RESCORE_ROLEPLAY_TRANSCRIPT_MOCK = gql`
  mutation RescoreRoleplayTranscriptMock($id: ID!) {
    rescoreRoleplayTranscriptMock(id: $id) {
      id
      rescoring
      scoredAt
    }
  }
`

// "Run again": a new mock from the same pasted call, reusing the copied
// employee, against the roleplay as it is now.
export const RERUN_ROLEPLAY_TRANSCRIPT_MOCK = gql`
  mutation RerunRoleplayTranscriptMock($id: ID!, $employeeVoice: String) {
    rerunRoleplayTranscriptMock(id: $id, employeeVoice: $employeeVoice) {
      id
      status
    }
  }
`

// Roleplay Copilot (see GET_ROLEPLAY_COPILOT in queries.ts).
export const CREATE_ROLEPLAY_COPILOT_THREAD = gql`
  mutation CreateRoleplayCopilotThread($templateTaskId: ID!) {
    createRoleplayCopilotThread(templateTaskId: $templateTaskId) {
      id
      title
    }
  }
`

export const SEND_ROLEPLAY_COPILOT_MESSAGE = gql`
  mutation SendRoleplayCopilotMessage(
    $threadId: ID
    $templateTaskId: ID
    $text: String!
  ) {
    sendRoleplayCopilotMessage(
      threadId: $threadId
      templateTaskId: $templateTaskId
      text: $text
    ) {
      id
      title
      activeTurn {
        id
        status
        stage
      }
    }
  }
`

export const STOP_ROLEPLAY_COPILOT_TURN = gql`
  mutation StopRoleplayCopilotTurn($threadId: ID!) {
    stopRoleplayCopilotTurn(threadId: $threadId) {
      id
      activeTurn {
        id
        status
        stopRequested
      }
    }
  }
`

export const DECIDE_ROLEPLAY_COPILOT_CHANGE = gql`
  mutation DecideRoleplayCopilotChange(
    $id: ID!
    $approve: Boolean!
    $reason: String
  ) {
    decideRoleplayCopilotChange(id: $id, approve: $approve, reason: $reason) {
      applied
      message
      change {
        id
        status
        errorMessage
        decidedByName
        decidedAt
      }
    }
  }
`

export const ADD_ROLEPLAY_COPILOT_DOCUMENT = gql`
  mutation AddRoleplayCopilotDocument(
    $templateTaskId: ID!
    $signedBlobId: String!
  ) {
    addRoleplayCopilotDocument(
      templateTaskId: $templateTaskId
      signedBlobId: $signedBlobId
    ) {
      id
      name
      status
    }
  }
`

export const DELETE_ROLEPLAY_COPILOT_DOCUMENT = gql`
  mutation DeleteRoleplayCopilotDocument($id: ID!) {
    deleteRoleplayCopilotDocument(id: $id)
  }
`

export const DELETE_ROLEPLAY_COPILOT_NOTE = gql`
  mutation DeleteRoleplayCopilotNote($id: ID!) {
    deleteRoleplayCopilotNote(id: $id)
  }
`

// ENGG-10202 — copy the Resources tab into an internal room (re-runnable).
export const IMPORT_RESOURCES_TAB_INTO_ROOM = gql`
  mutation ImportResourcesTabIntoRoom($roomId: ID) {
    importResourcesTabIntoRoom(roomId: $roomId) {
      room {
        id
        name
      }
      roomCreated
      sections
      links
      skipped
      restricted
      blocksWritten
      blocksRemoved
      documents
      items {
        resourceId
        title
        section
        outcome
        reason
      }
    }
  }
`

// ENGG-10243 — rename an internal room from its "Room settings" dialog (the
// DSR updateRoom; admins hold the editor floor on their internal rooms).
export const RENAME_INTERNAL_ROOM = gql`
  mutation RenameInternalRoom($id: ID!, $attributes: RoomInput!) {
    updateRoom(id: $id, attributes: $attributes) {
      id
      name
    }
  }
`

// ENGG-10243 — an internal room's place in the employee app: on the nav or
// not (turning it on publishes a draft), its 0-based position, the landing
// page, and its audience. An omitted argument leaves that setting as stored.
// Only the id comes back: the dialog re-reads the room on every open, and a
// field that failed to resolve must not hide a write that committed.
export const UPDATE_INTERNAL_ROOM_SETTINGS = gql`
  mutation UpdateInternalRoomSettings(
    $id: ID!
    $navEnabled: Boolean
    $navPosition: Int
    $landingPage: Boolean
    $audience: RoomAudienceInput
  ) {
    updateInternalRoomNav(
      id: $id
      navEnabled: $navEnabled
      navPosition: $navPosition
      landingPage: $landingPage
      audience: $audience
    ) {
      id
    }
  }
`

// Report Copilot: send a message (no sessionId starts a new conversation).
export const SEND_REPORT_COPILOT_MESSAGE = gql`
  mutation SendReportCopilotMessage($sessionId: ID, $message: String!) {
    sendReportCopilotMessage(sessionId: $sessionId, message: $message) {
      id
      status
      error
      transcript
      draft
    }
  }
`

// ENGG-10291 — who reads one tab of an internal room (the tab's "Who sees"
// dialog): the term lists only when changed, the flags always.
export const UPDATE_INTERNAL_ROOM_TAB_AUDIENCE = gql`
  mutation UpdateInternalRoomTabAudience(
    $id: ID!
    $audience: RoomTabAudienceInput!
  ) {
    updateRoomTabAudience(id: $id, audience: $audience) {
      id
    }
  }
`

// ENGG-8024 — Settings > Public Pages.
export const CREATE_PUBLIC_EMPLOYEE_FORM = gql`
  mutation CreatePublicEmployeeForm($attributes: PublicEmployeeFormInput!) {
    createPublicEmployeeForm(attributes: $attributes) {
      ${PUBLIC_EMPLOYEE_FORM_FIELDS}
    }
  }
`

export const UPDATE_PUBLIC_EMPLOYEE_FORM = gql`
  mutation UpdatePublicEmployeeForm(
    $id: ID!
    $attributes: PublicEmployeeFormInput!
  ) {
    updatePublicEmployeeForm(id: $id, attributes: $attributes) {
      ${PUBLIC_EMPLOYEE_FORM_FIELDS}
    }
  }
`

export const REGENERATE_PUBLIC_EMPLOYEE_FORM_LINK = gql`
  mutation RegeneratePublicEmployeeFormLink($id: ID!) {
    regeneratePublicEmployeeFormLink(id: $id) {
      ${PUBLIC_EMPLOYEE_FORM_FIELDS}
    }
  }
`

export const DELETE_PUBLIC_EMPLOYEE_FORM = gql`
  mutation DeletePublicEmployeeForm($id: ID!) {
    deletePublicEmployeeForm(id: $id)
  }
`

// Enrollable content: turn course-catalog enrollment on or off for a Content
// Library item. The first time it is turned on, the backend creates a hidden
// template with one task for the item.
export const SET_CONTENT_ENROLLABLE = gql`
  mutation SetContentEnrollable(
    $contentType: ContentEnrollmentType!
    $contentId: ID!
    $enrollable: Boolean!
  ) {
    setContentEnrollable(
      contentType: $contentType
      contentId: $contentId
      enrollable: $enrollable
    ) {
      enrollable
      templateCategory {
        id
        isEnrollable
      }
    }
  }
`

// ENGG-10357: an admin adds a scorecard to an employee's roleplay task — a new
// attempt stored like the AI's, so the existing highest-attempt rule decides
// whether a gated task passes.
export const ADD_ROLEPLAY_SCORECARD = gql`
  mutation AddRoleplayScorecard(
    $taskId: ID!
    $answers: [QuestionSubmitAnswer!]!
  ) {
    addRoleplayScorecard(taskId: $taskId, answers: $answers) {
      event {
        id
        eventType
        questionsSubmission {
          id
          score
          totalScore
        }
      }
      errors
    }
  }
`

// ENGG-10402: replace an existing roleplay attempt's scorecard with the admin's.
export const UPDATE_ROLEPLAY_SCORECARD = gql`
  mutation UpdateRoleplayScorecard($eventId: ID!, $answers: [QuestionSubmitAnswer!]!) {
    updateRoleplayScorecard(eventId: $eventId, answers: $answers) {
      event {
        id
        eventType
        questionsSubmission {
          id
          score
          totalScore
        }
      }
      errors
    }
  }
`
